import { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  GraduationCap, Plus, Star, MapPin, Clock, Eye, EyeOff, Globe,
  Mail, Users, BarChart3, ChevronRight, Check, X, Zap, Shield,
  Briefcase, Brain, TrendingUp, Award, Lock, Unlock,
  BookOpen, MessageSquare, Calendar, Settings, Send, RefreshCw,
  ArrowUpRight, CheckCircle, XCircle, AlertCircle, Info, ToggleLeft, ToggleRight,
  Layers, Lightbulb, Inbox, Sparkles, Video, Loader2
} from 'lucide-react';
import { ProgramsSection, BatchesSection, IdeasSection, SuggestIdeaDialog } from '@/components/mentors/MentorPrograms';
import { motion, AnimatePresence } from 'framer-motion';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, RadialBarChart, RadialBar
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { useSTNAuth } from '@/context/STNAuthContext';
import { hasFeature } from '@/lib/featureAccess';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  inviteMentor as apiInviteMentor,
  listMentors as apiListMentors,
  resendMentorInvite as apiResendInvite,
  patchMentorVisibility as apiPatchVisibility,
  getMentorAnalytics as apiGetMentorAnalytics,
  getMentorCalendar  as apiGetMentorCalendar,
  inferStnWebinarIntent, createStnWebinar, createStnWebinarRequest,
} from '@/api/stnApi';

// ---- Mock Data ----
const EXPERTISE_OPTIONS = [
  'AI Strategy', 'Product Management', 'Growth Marketing', 'Fundraising',
  'Tech Architecture', 'UX/Product Design', 'Business Development',
  'Operations & Scaling', 'Legal & Compliance', 'Finance & CFO',
  'HR & Culture Building', 'Enterprise Sales', 'Data Science',
  'DevOps & Infrastructure', 'Brand Building', 'VC Relations',
  'Cybersecurity', 'Blockchain / Web3', 'Marketplace Strategy',
  'Community Building', 'International Expansion', 'Supply Chain & Logistics',
  'HealthTech / MedTech', 'EdTech Strategy', 'FinTech Strategy',
  'AgriTech', 'CleanTech / Sustainability', 'Customer Success',
  'Pricing Strategy', 'PR & Media Relations', 'Performance Marketing',
  'SEO / SEM', 'Content Marketing', 'Social Media Strategy',
  'Partnership Development', 'Regulatory Affairs', 'Tax Planning',
  'Intellectual Property / Patents', 'Equity & ESOPs', 'Mobile Development',
  'Cloud Architecture', 'Product Analytics', 'E-commerce Strategy',
  'Diversity & Inclusion', 'Mental Health for Founders', 'Sales Enablement',
  'Competitive Intelligence',
];

// Status display helpers
const STATUS_META = {
  'Active':                       { label: 'Active',       cls: 'badge-success' },
  'Integration Setup Required':   { label: 'Setup Needed', cls: 'badge-info' },
  'Integration Requested':        { label: 'Active',       cls: 'badge-success' },
  'Under Review':                 { label: 'Under Review', cls: 'badge-info' },
  'Pending':                      { label: 'Invited',      cls: 'badge-warning' },
  'Rejected':                     { label: 'Rejected',     cls: 'badge-destructive' },
  'Blocked':                      { label: 'Blocked',      cls: 'bg-destructive/10 text-destructive border border-destructive/25' },
};
const statusMeta = (s) => STATUS_META[s] || { label: s || 'Unknown', cls: 'badge-muted' };
const isPending  = (s) => ['Pending'].includes(s);
const isReview   = (s) => ['Under Review'].includes(s);
const isActive   = (s) => ['Active', 'Integration Setup Required', 'Integration Requested'].includes(s);

const PUBLIC_MENTORS = [
  { id: 101, name: 'Sanjay Verma', expertise: ['B2B SaaS', 'Enterprise Sales'], exp: 18, rate: 3000, rating: 4.9, reviews: 67, city: 'Bangalore', available: true, sessions: 124, bio: 'Built a ₹500Cr ARR SaaS from scratch. Mentor for 50+ funded startups.' },
  { id: 102, name: 'Arpita Sen', expertise: ['UX/Product Design', 'Product Management'], exp: 14, rate: 2500, rating: 4.8, reviews: 45, city: 'Hyderabad', available: true, sessions: 89, bio: 'Ex-Design Lead at Amazon India. Helped 30+ startups achieve PMF.' },
  { id: 103, name: 'Vikash Tiwari', expertise: ['Tech Architecture', 'DevOps & Infrastructure'], exp: 16, rate: 2000, rating: 4.7, reviews: 38, city: 'Patna', available: false, sessions: 72, bio: 'Principal Architect at a FAANG company. Mentor focused on Bihar startups.' },
  { id: 104, name: 'Deepika Nair', expertise: ['Brand Building', 'Growth Marketing'], exp: 11, rate: 1800, rating: 4.9, reviews: 52, city: 'Mumbai', available: true, sessions: 98, bio: 'Ex-Marketing VP at Unacademy. Specializes in EdTech and D2C brands.' },
  { id: 105, name: 'Ashok Pandey', expertise: ['Legal & Compliance', 'VC Relations'], exp: 22, rate: 3500, rating: 4.6, reviews: 29, city: 'Patna', available: true, sessions: 56, bio: 'Senior Advocate & startup legal counsel. Handled 100+ DPIIT registrations.' },
  { id: 106, name: 'Meera Krishnan', expertise: ['Data Science', 'AI Strategy'], exp: 9, rate: 1500, rating: 4.8, reviews: 41, city: 'Chennai', available: true, sessions: 63, bio: 'PhD in ML from IIT Madras. Advises early-stage DeepTech startups.' },
];

// ---- Email Preview Dialog ----
const EmailPreviewDialog = ({ open, onClose, mentor }) => {
  const { startup } = useSTNAuth();
  if (!mentor) return null;
  const companyName = startup?.companyName || 'Your Company';
  const tempPw = mentor.tempPassword || `MNX@${String(mentor.name.length * 743 + 2026).slice(-4)}`;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-chart-3/15 flex items-center justify-center">
              <Mail className="w-5 h-5 text-chart-3" />
            </div>
            <div>
              <DialogTitle className="text-base">Email Sent to Mentor</DialogTitle>
              <p className="text-xs text-muted-foreground mt-0.5">Onboarding email delivered to {mentor.email}</p>
            </div>
          </div>
        </DialogHeader>

        <div className="mt-2 border border-border rounded-xl overflow-hidden text-sm">
          {/* Email Headers */}
          <div className="bg-muted/60 px-4 py-3 border-b border-border space-y-1.5">
            {[['From', 'noreply@mentornexus.io'], ['To', mentor.email], ['Subject', `Welcome to MentorNexus — ${companyName}`]].map(([k, v]) => (
              <div key={k} className="flex gap-2 text-xs">
                <span className="text-muted-foreground w-14 flex-shrink-0">{k}:</span>
                <span className="font-medium text-foreground">{v}</span>
              </div>
            ))}
          </div>

          {/* Email Body */}
          <div className="p-5 space-y-4 max-h-96 overflow-y-auto">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg gradient-primary flex items-center justify-center">
                <Zap className="w-3.5 h-3.5 text-primary-foreground" />
              </div>
              <span className="font-bold text-foreground text-sm">MentorNexus</span>
              <span className="text-xs text-muted-foreground">by StartupNexus</span>
            </div>

            <p className="text-sm text-foreground">Dear <strong>{mentor.name}</strong>,</p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              You've been invited to join the <strong className="text-foreground">MentorNexus program</strong> by{' '}
              <strong className="text-foreground">{companyName}</strong>. Your mentor profile has been set up and your account is ready.
            </p>

            {/* Credentials */}
            <div className="p-4 rounded-xl bg-primary/8 border border-primary/20">
              <p className="text-xs font-semibold text-foreground mb-3 flex items-center gap-2">
                <Shield className="w-3.5 h-3.5 text-primary" /> Login Credentials
              </p>
              <div className="space-y-2">
                {[['Email', mentor.email], ['Temporary Password', tempPw], ['Platform', 'mentornexus.startupnexus.in']].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{k}</span>
                    <span className="font-mono font-medium text-foreground bg-muted px-2 py-0.5 rounded">{v}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Expertise */}
            {mentor.expertise?.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Award className="w-3.5 h-3.5 text-chart-4" /> Expertise Areas
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {mentor.expertise.map(e => (
                    <span key={e} className="px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[11px] font-medium border border-primary/20">{e}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Settings */}
            <div className="border-t border-border pt-3 space-y-1.5">
              <p className="text-xs font-semibold text-foreground mb-2">Your Profile Settings:</p>
              {[
                ['Visibility', mentor.isPublic ? 'Publicly visible on MentorNexus' : `Private — ${companyName} only`],
                ['StartupNexus Batches', mentor.acceptsBatches ? 'Enabled — you may receive additional mentees' : 'Disabled'],
                ['Company Programs', mentor.companyPrograms ? `Enrolled in ${companyName} programs` : 'Not enrolled in programs'],
                ['NexusAI Network', mentor.nexusNetworkPublic ? `Listed publicly — earnings credited to ${companyName}` : 'Not listed in NexusAI public network'],
                ['External Mentoring', mentor.allowExternalMentoring ? 'Allowed outside company timings' : 'Company sessions only'],
              ].map(([k, v]) => (
                <div key={k} className="flex gap-2 text-xs">
                  <span className="text-muted-foreground w-36 flex-shrink-0">{k}:</span>
                  <span className="text-foreground">{v}</span>
                </div>
              ))}
            </div>

            <div className="pt-2">
              <div className="w-full py-2.5 rounded-xl gradient-primary text-center text-primary-foreground text-xs font-semibold cursor-pointer">
                Login to MentorNexus →
              </div>
            </div>

            <p className="text-xs text-muted-foreground border-t border-border pt-3">
              Warm regards,<br /><strong className="text-foreground">Team MentorNexus</strong> | StartupNexus Platform
            </p>
          </div>
        </div>

        <Button className="gradient-primary text-primary-foreground border-0 w-full" onClick={onClose}>
          Great, Email Delivered!
        </Button>
      </DialogContent>
    </Dialog>
  );
};

// ---- Onboard Mentor Dialog ----
const OnboardMentorDialog = ({ open, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    name: '', email: '', linkedin: '', bio: '', experience: '', rate: '',
    expertise: [],
    isPublic: false, acceptsBatches: false, companyPrograms: true,
    nexusNetworkPublic: false, allowExternalMentoring: false,
    days: [], sessionDuration: '60',
  });
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [otherText, setOtherText] = useState('');
  const [showOther, setShowOther] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleExpertise = (e) => set('expertise', form.expertise.includes(e) ? form.expertise.filter(x => x !== e) : [...form.expertise, e]);

  const addOther = () => {
    const val = otherText.trim();
    if (!val) return;
    if (!form.expertise.includes(val)) set('expertise', [...form.expertise, val]);
    setOtherText('');
    setShowOther(false);
  };

  const handleSubmit = async () => {
    if (!form.name || !form.email) { toast.error('Name and email are required'); return; }
    setSubmitting(true);
    try {
      const result = await apiInviteMentor(form);
      onSuccess({ ...form, tempPassword: result.tempPassword });
      setForm({ name: '', email: '', linkedin: '', bio: '', experience: '', rate: '', expertise: [], isPublic: false, acceptsBatches: false, companyPrograms: true, nexusNetworkPublic: false, allowExternalMentoring: false, days: [], sessionDuration: '60' });
      setOtherText('');
      setShowOther(false);
      setStep(1);
    } catch (err) {
      toast.error(err.message || 'Failed to invite mentor');
    } finally {
      setSubmitting(false);
    }
  };

  const resetAndClose = () => { setStep(1); setShowOther(false); setOtherText(''); onClose(); };

  return (
    <Dialog open={open} onOpenChange={resetAndClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-primary" />
            </div>
            <div>
              <DialogTitle>Onboard New Mentor</DialogTitle>
              <p className="text-xs text-muted-foreground mt-0.5">Step {step} of 3 — {['Mentor Details', 'Visibility & Access', 'Preview & Send'][step - 1]}</p>
            </div>
          </div>
          <div className="flex gap-1 mt-3">
            {[1, 2, 3].map(s => (
              <div key={s} className={cn('h-1 flex-1 rounded-full transition-all', s <= step ? 'bg-primary' : 'bg-muted')} />
            ))}
          </div>
        </DialogHeader>

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div key={1} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Full Name <span className="text-destructive">*</span></Label><Input className="mt-1" placeholder="Dr. Firstname Lastname" value={form.name} onChange={e => set('name', e.target.value)} /></div>
                <div><Label>Email Address <span className="text-destructive">*</span></Label><Input className="mt-1" type="email" placeholder="mentor@email.com" value={form.email} onChange={e => set('email', e.target.value)} /></div>
                <div><Label>LinkedIn / Website</Label><Input className="mt-1" placeholder="linkedin.com/in/..." value={form.linkedin} onChange={e => set('linkedin', e.target.value)} /></div>
                <div><Label>Experience (years)</Label><Input className="mt-1" type="number" placeholder="10" value={form.experience} onChange={e => set('experience', e.target.value)} /></div>
                <div><Label>Session Rate (₹/hr)</Label><Input className="mt-1" placeholder="2000 or Free" value={form.rate} onChange={e => set('rate', e.target.value)} /></div>
                <div className="col-span-2"><Label>Bio</Label><Textarea className="mt-1" rows={2} placeholder="Brief background, achievements, mentoring style..." value={form.bio} onChange={e => set('bio', e.target.value)} /></div>
              </div>
              <div>
                <Label className="mb-2 block">
                  Expertise Areas <span className="text-xs text-muted-foreground">(select all that apply)</span>
                  {form.expertise.length > 0 && (
                    <span className="ml-2 text-xs text-primary font-semibold">{form.expertise.length} selected</span>
                  )}
                </Label>
                <div className="flex flex-wrap gap-2">
                  {EXPERTISE_OPTIONS.map(e => (
                    <button key={e} type="button" onClick={() => toggleExpertise(e)}
                      className={cn('px-3 py-1.5 rounded-full text-xs font-medium border transition-all',
                        form.expertise.includes(e)
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground hover:border-primary/40'
                      )}>
                      {form.expertise.includes(e) && <Check className="w-3 h-3 inline mr-1" />}
                      {e}
                    </button>
                  ))}

                  {/* Custom "Other" chips already added */}
                  {form.expertise.filter(e => !EXPERTISE_OPTIONS.includes(e)).map(e => (
                    <button key={e} type="button" onClick={() => toggleExpertise(e)}
                      className="px-3 py-1.5 rounded-full text-xs font-medium border border-primary bg-primary/10 text-primary transition-all flex items-center gap-1">
                      <Check className="w-3 h-3" />{e}
                      <X className="w-3 h-3 ml-0.5 opacity-60" />
                    </button>
                  ))}

                  {/* Other toggle */}
                  {!showOther ? (
                    <button type="button" onClick={() => setShowOther(true)}
                      className="px-3 py-1.5 rounded-full text-xs font-medium border border-dashed border-border text-muted-foreground hover:border-primary/40 hover:text-foreground transition-all flex items-center gap-1">
                      <Plus className="w-3 h-3" /> Other
                    </button>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <Input
                        autoFocus
                        className="h-7 text-xs w-40 rounded-full px-3"
                        placeholder="Type expertise…"
                        value={otherText}
                        onChange={e => setOtherText(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addOther(); } if (e.key === 'Escape') { setShowOther(false); setOtherText(''); } }}
                      />
                      <button type="button" onClick={addOther}
                        className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => { setShowOther(false); setOtherText(''); }}
                        className="w-7 h-7 rounded-full border border-border text-muted-foreground flex items-center justify-center flex-shrink-0">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end">
                <Button className="gradient-primary text-primary-foreground border-0" onClick={() => { if (!form.name || !form.email) { toast.error('Name and email required'); return; } setStep(2); }}>
                  Next: Visibility Settings <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div key={2} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-5 mt-2">
              {/* Info Banner */}
              <div className="p-3.5 rounded-xl bg-info/8 border border-info/20 flex gap-3">
                <Info className="w-4 h-4 text-info flex-shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground">
                  These settings control how this mentor is visible across StartupNexus. Choose carefully — once a mentor is made public, they can receive session requests from any startup.
                </p>
              </div>

              {/* Flag 1: StartupNexus Batches */}
              <div className={cn('p-5 rounded-xl border transition-all', form.acceptsBatches ? 'border-primary/30 bg-primary/5' : 'border-border bg-muted/30')}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-7 h-7 rounded-lg bg-chart-2/10 flex items-center justify-center">
                        <Zap className="w-3.5 h-3.5 text-chart-2" />
                      </div>
                      <p className="font-semibold text-sm text-foreground">Accept batches from StartupNexus?</p>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      If <strong>enabled</strong>, StartupNexus may assign additional mentee batches to this mentor from the MentorNexus ecosystem pool — beyond just your company's team.
                      The mentor will be notified of new batch assignments by email.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      If <strong>disabled</strong>, the mentor only serves your company's employees.
                    </p>
                  </div>
                  <Switch checked={form.acceptsBatches} onCheckedChange={v => set('acceptsBatches', v)} />
                </div>
                {form.acceptsBatches && (
                  <div className="mt-3 p-2.5 rounded-lg bg-chart-2/8 border border-chart-2/20">
                    <p className="text-xs text-chart-2 font-medium">Batch acceptance enabled — StartupNexus may assign cross-platform mentees</p>
                  </div>
                )}
              </div>

              {/* Flag 2: Public Visibility */}
              <div className={cn('p-5 rounded-xl border transition-all', form.isPublic ? 'border-success/30 bg-success/5' : 'border-border bg-muted/30')}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-7 h-7 rounded-lg bg-success/10 flex items-center justify-center">
                        {form.isPublic ? <Eye className="w-3.5 h-3.5 text-success" /> : <EyeOff className="w-3.5 h-3.5 text-muted-foreground" />}
                      </div>
                      <p className="font-semibold text-sm text-foreground">Make publicly visible for sessions?</p>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      If <strong>enabled</strong>, this mentor will appear in <strong>public MentorNexus listings</strong>, partner dropdowns, and can be booked by any startup on the platform.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      If <strong>disabled</strong>, this mentor is <strong>exclusive to your company</strong> — their profile won't appear in any public search, listing, or dropdown.
                    </p>
                  </div>
                  <Switch checked={form.isPublic} onCheckedChange={v => set('isPublic', v)} />
                </div>
                <div className={cn('mt-3 p-2.5 rounded-lg text-xs font-medium', form.isPublic ? 'bg-success/8 border border-success/20 text-success' : 'bg-muted/50 border border-border text-muted-foreground')}>
                  {form.isPublic ? '🟢 Publicly listed — visible in MentorNexus directory, partner pages, session booking dropdowns' : '🔒 Private — exclusive to your company, hidden from all public listings'}
                </div>
              </div>

              {/* Flag 3: Company Programs */}
              <div className={cn('p-5 rounded-xl border transition-all', form.companyPrograms ? 'border-primary/30 bg-primary/5' : 'border-border bg-muted/30')}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                        <BookOpen className="w-3.5 h-3.5 text-primary" />
                      </div>
                      <p className="font-semibold text-sm text-foreground">Available for company programs?</p>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      If <strong>enabled</strong>, this mentor's profile will appear in <strong>your company's internal program dropdowns</strong> (e.g. SkillNexus batches, TCI programs, onboarding tracks). <br />
                      If <strong>disabled</strong>, their details won't populate in any company-related program listing.
                    </p>
                  </div>
                  <Switch checked={form.companyPrograms} onCheckedChange={v => set('companyPrograms', v)} />
                </div>
              </div>

              {/* Flag 4: NexusAI Public Network */}
              <div className={cn('p-5 rounded-xl border transition-all', form.nexusNetworkPublic ? 'border-chart-4/30 bg-chart-4/5' : 'border-border bg-muted/30')}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className={cn('w-7 h-7 rounded-lg flex items-center justify-center', form.nexusNetworkPublic ? 'bg-chart-4/15' : 'bg-muted')}>
                        <Globe className="w-3.5 h-3.5 text-chart-4" />
                      </div>
                      <p className="font-semibold text-sm text-foreground">List in NexusAI public network?</p>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      If <strong>enabled</strong>, this mentor is visible to <strong>all startups on the NexusAI platform</strong>. Session earnings from external bookings are <strong className="text-foreground">credited to your company account</strong>.
                    </p>
                    {form.nexusNetworkPublic && (
                      <div className="mt-2 p-2.5 rounded-lg bg-chart-4/8 border border-chart-4/20">
                        <p className="text-xs text-chart-4 font-medium">💰 Earnings model: fees from external sessions → company wallet</p>
                      </div>
                    )}
                  </div>
                  <Switch checked={form.nexusNetworkPublic} onCheckedChange={v => set('nexusNetworkPublic', v)} />
                </div>
              </div>

              {/* Flag 5: External Mentoring outside office hours */}
              <div className={cn('p-5 rounded-xl border transition-all', form.allowExternalMentoring ? 'border-chart-2/30 bg-chart-2/5' : 'border-border bg-muted/30')}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className={cn('w-7 h-7 rounded-lg flex items-center justify-center', form.allowExternalMentoring ? 'bg-chart-2/15' : 'bg-muted')}>
                        <Clock className="w-3.5 h-3.5 text-chart-2" />
                      </div>
                      <p className="font-semibold text-sm text-foreground">Allow external mentoring outside office hours?</p>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      If <strong>enabled</strong>, this mentor can create a <strong>personal profile</strong> on MentorNexus and take external sessions <strong>outside company-defined timings</strong>. Their personal earnings go directly to them.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      If <strong>disabled</strong>, the mentor's availability is locked to company timings only.
                    </p>
                  </div>
                  <Switch checked={form.allowExternalMentoring} onCheckedChange={v => set('allowExternalMentoring', v)} />
                </div>
              </div>

              <div className="flex gap-3 pt-1">
                <Button variant="outline" className="border-border" onClick={() => setStep(1)}><ChevronRight className="w-4 h-4 rotate-180 mr-1" /> Back</Button>
                <Button className="flex-1 gradient-primary text-primary-foreground border-0" onClick={() => setStep(3)}>Review & Send Email <ChevronRight className="w-4 h-4 ml-1" /></Button>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div key={3} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="space-y-4 mt-2">
              {/* Summary */}
              <div className="space-y-3">
                <div className="p-4 rounded-xl bg-muted/50 border border-border">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center text-sm font-bold text-primary-foreground">
                      {form.name ? form.name.split(' ').slice(0, 2).map(n => n[0]).join('') : 'M'}
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{form.name || 'Mentor Name'}</p>
                      <p className="text-xs text-muted-foreground">{form.email}</p>
                    </div>
                  </div>
                  {form.expertise.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {form.expertise.map(e => <span key={e} className="badge-primary text-xs">{e}</span>)}
                    </div>
                  )}
                </div>

                {/* Flags summary */}
                <div className="p-4 rounded-xl border border-border space-y-3">
                  <p className="text-xs font-semibold text-foreground">Visibility & Access Summary:</p>
                  {[
                    { label: 'Accept StartupNexus Batches', value: form.acceptsBatches, on: 'Yes — may receive cross-platform mentees', off: 'No — company-only mentees' },
                    { label: 'Public Visibility', value: form.isPublic, on: 'Public — listed in MentorNexus directory', off: 'Private — hidden from all public listings' },
                    { label: 'Company Programs', value: form.companyPrograms, on: 'Visible in company program dropdowns', off: 'Not in company program listings' },
                    { label: 'NexusAI Public Network', value: form.nexusNetworkPublic, on: 'Listed publicly — earnings → company wallet', off: 'Not in public NexusAI network' },
                    { label: 'External Mentoring (outside hours)', value: form.allowExternalMentoring, on: 'Allowed — mentor can take personal sessions', off: 'Not allowed outside company timings' },
                  ].map(f => (
                    <div key={f.label} className="flex items-start justify-between gap-3">
                      <span className="text-xs text-muted-foreground">{f.label}</span>
                      <span className={cn('text-xs font-medium flex-shrink-0', f.value ? 'text-success' : 'text-muted-foreground')}>
                        {f.value ? f.on : f.off}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="p-3.5 rounded-xl bg-chart-3/8 border border-chart-3/20">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-chart-3" />
                    <p className="text-xs font-medium text-foreground">Onboarding email will be sent to <strong>{form.email || 'mentor email'}</strong></p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Email contains: login credentials, assigned mentees list, platform access link, and their visibility settings.</p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button variant="outline" className="border-border" onClick={() => setStep(2)}><ChevronRight className="w-4 h-4 rotate-180 mr-1" /> Back</Button>
                <Button
                  className="flex-1 gradient-primary text-primary-foreground border-0 h-11 font-semibold"
                  onClick={handleSubmit} disabled={submitting}
                >
                  {submitting ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                      Sending email...
                    </span>
                  ) : <><Send className="w-4 h-4 mr-2" /> Onboard & Send Email</>}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

// ---- Program Detail Card (used inside MentorAnalyticsSheet Programs tab) ----
const DOMAIN_COLOR = {
  nep_general: 'badge-primary', it: 'badge-info', medical: 'bg-red-50 border-red-200 text-red-700',
  engineering: 'bg-orange-50 border-orange-200 text-orange-700',
  design: 'bg-purple-50 border-purple-200 text-purple-700',
  hospitality: 'bg-yellow-50 border-yellow-200 text-yellow-700',
};
const domainBadge = (d) => DOMAIN_COLOR[d] || 'badge-muted';

const ProgramDetailCard = ({ prog }) => {
  const [expanded, setExpanded] = useState(false);
  const hasExtra = prog.jobRole || prog.focusArea || (prog.aiTools?.length > 0) || (prog.industries?.length > 0) || (prog.careerPaths?.length > 0) || prog.notes;

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      {/* Header row */}
      <div className="p-4 flex gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <BookOpen className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground leading-snug">{prog.title}</p>
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            {prog.domain && (
              <span className={cn('text-[11px] px-2 py-0.5 rounded-full border font-medium', domainBadge(prog.domain))}>
                {prog.domain.replace(/_/g, ' ')}
              </span>
            )}
            {prog.level && (
              <span className="text-[11px] px-2 py-0.5 rounded-full border bg-muted text-muted-foreground font-medium">
                {prog.level}
              </span>
            )}
            {prog.totalWeeks && (
              <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" />{prog.totalWeeks} weeks
              </span>
            )}
            {prog.programType && (
              <span className="text-[11px] text-muted-foreground">{prog.programType}</span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <span className="badge-success text-[11px]">Assigned</span>
          {prog.assignedAt && (
            <span className="text-[10px] text-muted-foreground">
              {new Date(prog.assignedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          )}
        </div>
      </div>

      {/* Level description */}
      {prog.levelDesc && (
        <p className="text-xs text-muted-foreground px-4 pb-3 -mt-1">{prog.levelDesc}</p>
      )}

      {/* Expandable details */}
      {hasExtra && (
        <>
          <div className="border-t border-border/50 px-4 py-2">
            <button
              onClick={() => setExpanded(e => !e)}
              className="text-xs text-primary font-medium flex items-center gap-1 hover:underline"
            >
              {expanded ? 'Hide details' : 'View full details'}
              <ChevronRight className={cn('w-3.5 h-3.5 transition-transform', expanded && 'rotate-90')} />
            </button>
          </div>

          {expanded && (
            <div className="px-4 pb-4 space-y-3 border-t border-border/50 pt-3">
              {prog.jobRole && (
                <div>
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Job Role</p>
                  <p className="text-sm text-foreground">{prog.jobRole}</p>
                </div>
              )}
              {prog.focusArea && (
                <div>
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Focus Area</p>
                  <p className="text-sm text-foreground">{prog.focusArea}</p>
                </div>
              )}
              {prog.aiTools?.length > 0 && (
                <div>
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">AI Tools</p>
                  <div className="flex flex-wrap gap-1.5">
                    {prog.aiTools.map((t, i) => (
                      <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-primary/8 border border-primary/20 text-primary font-medium">{t}</span>
                    ))}
                  </div>
                </div>
              )}
              {prog.industries?.length > 0 && (
                <div>
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Target Industries</p>
                  <div className="flex flex-wrap gap-1.5">
                    {prog.industries.map((t, i) => (
                      <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-muted text-muted-foreground">{t}</span>
                    ))}
                  </div>
                </div>
              )}
              {prog.careerPaths?.length > 0 && (
                <div>
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Career Paths</p>
                  <div className="flex flex-wrap gap-1.5">
                    {prog.careerPaths.map((t, i) => (
                      <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-success/8 border border-success/20 text-success">{t}</span>
                    ))}
                  </div>
                </div>
              )}
              {prog.notes && (
                <div>
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Admin Notes</p>
                  <p className="text-xs text-foreground italic bg-muted/40 px-3 py-2 rounded-lg">{prog.notes}</p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ---- Batch status helpers ----
const BATCH_STATUS_META = {
  active:            { label: 'Active',           cls: 'bg-success/10 border-success/25 text-success' },
  upcoming:          { label: 'Upcoming',          cls: 'bg-info/10 border-info/25 text-info' },
  completed:         { label: 'Completed',         cls: 'bg-muted text-muted-foreground border-border' },
  draft:             { label: 'Draft',             cls: 'bg-muted text-muted-foreground border-border' },
  pending_approval:  { label: 'Pending Approval',  cls: 'bg-warning/10 border-warning/25 text-warning' },
  under_review:      { label: 'Under Review',      cls: 'bg-warning/10 border-warning/25 text-warning' },
  cancelled:         { label: 'Cancelled',         cls: 'bg-destructive/10 border-destructive/25 text-destructive' },
  rejected:          { label: 'Rejected',          cls: 'bg-destructive/10 border-destructive/25 text-destructive' },
  disabled:          { label: 'Disabled',          cls: 'bg-muted text-muted-foreground border-border' },
};
const batchStatusMeta = (s) => BATCH_STATUS_META[s] || { label: s || 'Unknown', cls: 'bg-muted text-muted-foreground border-border' };

const TA_TYPE_LABEL   = { ita: 'ITA — Tech Support', sme: 'SME — Subject Expert' };
const TA_STATUS_META  = {
  pending:   { cls: 'bg-warning/10 text-warning border-warning/25',   label: 'Pending' },
  approved:  { cls: 'bg-info/10 text-info border-info/25',            label: 'Approved' },
  assigned:  { cls: 'bg-success/10 text-success border-success/25',   label: 'Assigned' },
  completed: { cls: 'bg-muted text-muted-foreground border-border',   label: 'Completed' },
  cancelled: { cls: 'bg-destructive/10 text-destructive border-destructive/25', label: 'Cancelled' },
};

const SESSION_TYPE_COLOR = {
  live: 'text-primary', doubt: 'text-warning', support: 'text-chart-3',
  adhoc: 'text-chart-4', recorded: 'text-muted-foreground',
};

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}
function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
function fmtTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}
function fmtTimeRange(scheduledAt, durationMinutes) {
  if (!scheduledAt) return '—';
  const start = new Date(scheduledAt);
  const end   = new Date(start.getTime() + (durationMinutes || 60) * 60000);
  const opts  = { hour: '2-digit', minute: '2-digit', hour12: true };
  return `${start.toLocaleTimeString('en-IN', opts)} – ${end.toLocaleTimeString('en-IN', opts)}`;
}
function minsToHours(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

// ---- Webinar hosting — dynamic intent-driven stepper (Wave 5) ----
// Step 1: free text ("what are you hosting this for?") -> AI infers a
// suggested title, sector, and which optional fields are even relevant.
// Step 2: only AI-selected fields render, and as pickers (sector dropdown,
// experience as a bucketed range) rather than more free text. Step 3: review
// + submit. Shared between self-serve hosting (mode="self") and the
// admin-assisted cohort request (mode="request") since Step 1/2 are
// identical — only what gets submitted at the end, and a couple of
// mode-specific scheduling fields, differ.
const WEBINAR_VISIBILITY_OPTIONS = [
  { value: 'internnexus',  label: 'InternNexus (candidates)' },
  { value: 'skillnexus',   label: 'SkillNexus' },
  { value: 'promptnexus',  label: 'PromptNexus' },
  { value: 'startupnexus', label: 'Other Startups' },
];
const WEBINAR_SECTORS = [
  'Technology', 'Design', 'Marketing', 'Sales', 'Finance', 'Operations',
  'Legal', 'HR & People', 'Education', 'Arts & Media', 'Healthcare', 'Other',
];
const WEBINAR_EXPERIENCE_RANGES = ['0-1', '1-3', '3-5', '5+'];

const WebinarIntentStepper = ({ open, onClose, mode, mentor, hasActiveMentor, onInviteMentor, onHosted }) => {
  const [step, setStep]             = useState(1);
  const [purposeText, setPurposeText] = useState('');
  const [inferring, setInferring]   = useState(false);
  const [relevantFields, setRelevantFields] = useState([]);

  const [title, setTitle]           = useState('');
  const [sector, setSector]         = useState('Other');
  const [experienceRange, setExperienceRange] = useState('');
  const [audienceSize, setAudienceSize]       = useState('');
  const [location, setLocation]     = useState('');
  const [description, setDescription] = useState('');

  // self-serve only
  const [scheduledAt, setScheduledAt] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [visibility, setVisibility] = useState(['internnexus']);
  // admin-request only
  const [dateFrom, setDateFrom]     = useState('');
  const [dateTo, setDateTo]         = useState('');

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(1); setPurposeText(''); setInferring(false); setRelevantFields([]);
    setTitle(''); setSector('Other'); setExperienceRange(''); setAudienceSize('');
    setLocation(''); setDescription('');
    setScheduledAt(''); setDurationMinutes(60); setVisibility(['internnexus']);
    setDateFrom(''); setDateTo('');
  }, [open, mentor?._id, mode]);

  if (mode === 'self' && !mentor) return null;

  const toggleVis = (v) => setVisibility((prev) => prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]);

  const continueFromStep1 = async () => {
    if (!purposeText.trim()) return toast.error("Tell us what you're hosting this for.");
    setInferring(true);
    try {
      const r = await inferStnWebinarIntent(purposeText.trim());
      setTitle(r?.suggestedTitle || purposeText.trim().slice(0, 80));
      setSector(WEBINAR_SECTORS.includes(r?.sector) ? r.sector : 'Other');
      setExperienceRange(r?.experienceRange || '');
      setRelevantFields(Array.isArray(r?.relevantFields) ? r.relevantFields : []);
    } catch {
      // AI is best-effort — never block the stepper on it
      setTitle(purposeText.trim().slice(0, 80));
      setSector('Other');
      setExperienceRange('');
      setRelevantFields(['experienceRange', 'audienceSize']);
    } finally {
      setInferring(false);
      setStep(2);
    }
  };

  const continueFromStep2 = () => {
    if (!title.trim()) return toast.error('Title is required.');
    if (!location.trim()) return toast.error('Location is required.');
    if (mode === 'self') {
      if (!scheduledAt) return toast.error('Pick a date & time.');
      if (!visibility.length) return toast.error('Pick at least one platform to list this on.');
    }
    setStep(3);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      if (mode === 'self') {
        const fullDescription = [
          description,
          sector ? `Sector: ${sector}` : null,
          experienceRange ? `Experience: ${experienceRange} years` : null,
          `Location: ${location}`,
          audienceSize ? `Expected audience: ${audienceSize}` : null,
        ].filter(Boolean).join('\n');
        await createStnWebinar({
          title: title.trim(), description: fullDescription, scheduledAt: new Date(scheduledAt).toISOString(),
          durationMinutes: Number(durationMinutes) || 60,
          mentorProfileId: mentor._id, meetingProvider: 'teams', visibility,
        });
        toast.success('Webinar created and listed.');
        onHosted && onHosted();
      } else {
        await createStnWebinarRequest({
          purposeText: purposeText.trim(), location,
          expectedAudienceSize: audienceSize ? Number(audienceSize) : null,
          desiredDateRange: { from: dateFrom || null, to: dateTo || null },
          inferredSector: sector, inferredExperienceRange: experienceRange,
        });
        toast.success("Request sent — admin will reach out once it's scheduled.");
      }
      onClose();
    } catch (e) {
      toast.error(e?.message || `Could not ${mode === 'self' ? 'create webinar' : 'submit request'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const titleText = mode === 'self' ? `Host a Webinar — ${mentor?.name || ''}` : 'Request a Cohort Webinar';
  const titleIcon = mode === 'self' ? <Video className="w-4 h-4 text-primary" /> : <Inbox className="w-4 h-4 text-primary" />;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{titleIcon} {titleText}</DialogTitle>
          <p className="text-[11px] text-muted-foreground">Step {step} of 3</p>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3">
            {mode === 'request' && !hasActiveMentor && (
              <div className="flex items-center justify-between gap-3 p-3 rounded-xl bg-primary/5 border border-primary/20">
                <p className="text-xs text-muted-foreground">Have your own mentor instead? You can host it yourself.</p>
                <Button size="sm" variant="outline" className="border-primary/30 text-primary shrink-0" onClick={onInviteMentor}>
                  Invite a Mentor
                </Button>
              </div>
            )}
            <div>
              <Label>What are you hosting this for?</Label>
              <Textarea className="mt-1" rows={3} value={purposeText} onChange={(e) => setPurposeText(e.target.value)}
                placeholder="e.g. I want to hire a music teacher for our upcoming batch" />
            </div>
            <Button className="w-full gradient-primary text-primary-foreground border-0" onClick={continueFromStep1} disabled={inferring}>
              {inferring ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5 inline" /> Thinking…</> : 'Continue'}
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <div>
              <Label>Title</Label>
              <Input className="mt-1" value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Sector</Label>
                <Select value={sector} onValueChange={setSector}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {WEBINAR_SECTORS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Location</Label>
                <Input className="mt-1" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="City, state, or Remote" />
              </div>
            </div>

            {relevantFields.includes('experienceRange') && (
              <div>
                <Label className="mb-1.5 block">Experience level</Label>
                <div className="flex flex-wrap gap-2">
                  {WEBINAR_EXPERIENCE_RANGES.map((r) => (
                    <button key={r} type="button" onClick={() => setExperienceRange(r)}
                      className={cn('text-xs px-3 py-1.5 rounded-full border font-medium transition-colors',
                        experienceRange === r ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary/40')}>
                      {r} yrs
                    </button>
                  ))}
                </div>
              </div>
            )}

            {relevantFields.includes('audienceSize') && (
              <div>
                <Label>Expected audience size</Label>
                <Input className="mt-1" type="number" min={1} value={audienceSize} onChange={(e) => setAudienceSize(e.target.value)} />
              </div>
            )}

            <div>
              <Label>Description</Label>
              <Textarea className="mt-1" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Anything else worth mentioning?" />
            </div>

            {mode === 'self' ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Date & time</Label>
                    <Input className="mt-1" type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
                  </div>
                  <div>
                    <Label>Duration (minutes)</Label>
                    <Input className="mt-1" type="number" min={15} step={15} value={durationMinutes} onChange={(e) => setDurationMinutes(e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label className="mb-1.5 block">List on</Label>
                  <div className="flex flex-wrap gap-2">
                    {WEBINAR_VISIBILITY_OPTIONS.map((o) => (
                      <button key={o.value} type="button" onClick={() => toggleVis(o.value)}
                        className={cn('text-xs px-3 py-1.5 rounded-full border font-medium transition-colors',
                          visibility.includes(o.value) ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary/40')}>
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Earliest date</Label>
                  <Input className="mt-1" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </div>
                <div>
                  <Label>Latest date</Label>
                  <Input className="mt-1" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setStep(1)}>Back</Button>
              <Button className="flex-1 gradient-primary text-primary-foreground border-0" onClick={continueFromStep2}>Continue</Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <div className="rounded-xl border border-border p-4 space-y-1.5 text-sm">
              <p><span className="text-muted-foreground">Title:</span> <span className="font-medium text-foreground">{title}</span></p>
              <p><span className="text-muted-foreground">Sector:</span> {sector}</p>
              {experienceRange && <p><span className="text-muted-foreground">Experience:</span> {experienceRange} yrs</p>}
              {audienceSize && <p><span className="text-muted-foreground">Audience:</span> {audienceSize}</p>}
              <p><span className="text-muted-foreground">Location:</span> {location}</p>
              {mode === 'self' ? (
                <>
                  <p><span className="text-muted-foreground">When:</span> {scheduledAt ? new Date(scheduledAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'} ({durationMinutes} min)</p>
                  <p><span className="text-muted-foreground">Listed on:</span> {visibility.join(', ') || '—'}</p>
                </>
              ) : (
                <p><span className="text-muted-foreground">Preferred window:</span> {dateFrom || '—'} to {dateTo || '—'}</p>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setStep(2)}>Back</Button>
              <Button className="flex-1 gradient-primary text-primary-foreground border-0" onClick={submit} disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === 'self' ? 'Create Webinar' : 'Submit Request')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const HostWebinarModal = ({ open, onClose, mentor, onHosted }) => (
  <WebinarIntentStepper mode="self" open={open} onClose={onClose} mentor={mentor} onHosted={onHosted} />
);

const RequestCohortWebinarModal = ({ open, onClose, hasActiveMentor, onInviteMentor }) => (
  <WebinarIntentStepper mode="request" open={open} onClose={onClose} hasActiveMentor={hasActiveMentor} onInviteMentor={onInviteMentor} />
);

// ---- Mentor Detail Sheet (tabbed) ----
const MentorAnalyticsSheet = ({ mentor, open, onClose, onResend }) => {
  const [detailTab, setDetailTab]       = useState('overview');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError]     = useState(null);
  const [expandedBatch, setExpandedBatch]       = useState(null);
  const [batchSubTab, setBatchSubTab]           = useState({});

  useEffect(() => {
    if (open && mentor?._id) {
      setAnalyticsData(null);
      setAnalyticsError(null);
      setExpandedBatch(null);
      setBatchSubTab({});
      setAnalyticsLoading(true);
      apiGetMentorAnalytics(mentor._id)
        .then(d => setAnalyticsData(d))
        .catch(e => setAnalyticsError(e.message))
        .finally(() => setAnalyticsLoading(false));
    }
  }, [open, mentor?._id]);

  if (!mentor) return null;
  const meta = statusMeta(mentor.status);
  const rateLabel = mentor.approvedRateMin != null
    ? `₹${Number(mentor.approvedRateMin).toLocaleString()}${mentor.approvedRateMax != null && mentor.approvedRateMax !== mentor.approvedRateMin ? `–₹${Number(mentor.approvedRateMax).toLocaleString()}` : ''}/hr (approved)`
    : mentor.submittedRate > 0 ? `₹${Number(mentor.submittedRate).toLocaleString()}/hr (submitted)` : 'Not set';

  const evalReport = mentor.mentorevalReport;
  const aiR        = mentor.aiReports || {};
  const programs   = mentor.assignedPrograms || [];
  const isActiveStatus  = mentor.status === 'Active' || mentor.status === 'Integration Setup Required';
  const evalWasRejected = mentor.mentorevalDecision === 'REJECTED' || mentor.mentorevalDecision === 'CONDITIONALLY_REJECTED';
  const isManualOverride = isActiveStatus && evalWasRejected;

  const batches   = analyticsData?.batches || [];
  const summary   = analyticsData?.summary || {};
  const bSubTab   = (bid) => batchSubTab[bid] || 'sessions';
  const setBSubTab= (bid, tab) => setBatchSubTab(p => ({ ...p, [bid]: tab }));

  // Compute accurate stats from analytics data (overrides profile-stored zeros)
  const computedTotalMins = batches.reduce((total, b) =>
    total + (b.allSessions || []).filter(s => s.status === 'completed')
      .reduce((t, s) => t + (s.durationMinutes || 60), 0), 0);
  const kpiSessions = analyticsData ? (summary.completedSessions || 0) : (mentor.sessions || 0);
  const kpiHours    = analyticsData ? computedTotalMins : ((mentor.hours || 0) * 60);
  const kpiMentees  = analyticsData ? (summary.totalEnrollments || 0) : (mentor.mentees || 0);

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto p-0 flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-border bg-card/80">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-full gradient-primary flex items-center justify-center text-sm font-bold text-primary-foreground flex-shrink-0">
              {mentor.name.split(' ').slice(0, 2).map(n => n[0]).join('')}
            </div>
            <div className="flex-1 min-w-0">
              <SheetTitle className="text-base">{mentor.name}</SheetTitle>
              <p className="text-xs text-muted-foreground">{mentor.email}</p>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium', meta.cls)}>{meta.label}</span>
                {mentor.rating > 0 && <span className="flex items-center gap-0.5 text-xs"><Star className="w-3 h-3 fill-warning text-warning" />{mentor.rating}</span>}
                <span className={cn('text-xs px-2 py-0.5 rounded-full border font-medium', mentor.isPublic ? 'bg-success/10 text-success border-success/25' : 'bg-muted text-muted-foreground border-border')}>
                  {mentor.isPublic ? 'Public' : 'Private'}
                </span>
                <span className="text-xs text-muted-foreground font-medium">{rateLabel}</span>
              </div>
            </div>
          </div>
          {/* KPI Row */}
          <div className="grid grid-cols-4 gap-2 mt-4">
            {[
              { label: 'Sessions',    value: kpiSessions,                    icon: Calendar, color: 'text-chart-1', bg: 'bg-chart-1/10' },
              { label: 'Total Hours', value: minsToHours(kpiHours),          icon: Clock,    color: 'text-chart-2', bg: 'bg-chart-2/10' },
              { label: 'Mentees',     value: kpiMentees,                     icon: Users,    color: 'text-chart-3', bg: 'bg-chart-3/10' },
              { label: 'Rating',      value: mentor.rating > 0 ? mentor.rating : '—', icon: Star, color: 'text-chart-4', bg: 'bg-chart-4/10' },
            ].map(k => (
              <div key={k.label} className={cn('p-2.5 rounded-xl border border-border text-center', k.bg)}>
                <k.icon className={cn('w-3.5 h-3.5 mx-auto mb-1', k.color)} />
                <div className="font-bold text-foreground text-sm">{k.value}</div>
                <div className="text-[11px] text-muted-foreground">{k.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Tabs nav */}
        <div className="border-b border-border bg-muted/30 px-4 pt-3 flex gap-1 overflow-x-auto flex-shrink-0">
          {[
            { id: 'overview',  label: 'Overview' },
            { id: 'programs',  label: `Programs (${programs.length})` },
            { id: 'batches',   label: batches.length > 0 ? `Batches (${batches.length})` : 'Batches' },
            { id: 'analytics', label: 'Analytics' },
            { id: 'eval',      label: 'Profile Eval' },
          ].map(t => (
            <button key={t.id} onClick={() => setDetailTab(t.id)}
              className={cn('px-3 py-2 text-xs font-semibold rounded-t-lg whitespace-nowrap border-b-2 transition-colors',
                detailTab === t.id
                  ? 'border-primary text-primary bg-background'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              )}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto p-5">

          {/* ── Overview ── */}
          {detailTab === 'overview' && (
            <div className="space-y-4">
              {mentor.bio && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Bio</p>
                  <p className="text-sm text-foreground leading-relaxed">{mentor.bio}</p>
                </div>
              )}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Expertise</p>
                <div className="flex flex-wrap gap-1.5">
                  {(mentor.expertise || []).map(e => <span key={e} className="badge-primary text-xs">{e}</span>)}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ['Experience', `${mentor.experience || 0} years`],
                  ['LinkedIn', mentor.linkedinUrl || 'Not set'],
                  ['Joined', mentor.joined || '—'],
                  ['Rate', rateLabel],
                  ['NexusAI Network', mentor.isPublic ? 'Publicly listed' : 'Private'],
                  ['External Mentoring', mentor.allowExternal ? 'Allowed' : 'Not allowed'],
                ].map(([k, v]) => (
                  <div key={k} className="p-3 rounded-xl bg-muted/40 border border-border">
                    <p className="text-[11px] text-muted-foreground font-medium mb-0.5">{k}</p>
                    <p className="text-sm font-semibold text-foreground truncate">{v}</p>
                  </div>
                ))}
              </div>
              {/* Pending actions */}
              {isPending(mentor.status) && (
                <div className="p-4 rounded-xl bg-warning/8 border border-warning/20">
                  <p className="text-sm font-semibold text-warning mb-1">Invitation Pending</p>
                  <p className="text-xs text-muted-foreground mb-3">Mentor hasn't logged in yet. Send a fresh invite.</p>
                  <Button variant="outline" size="sm" className="border-warning/30 text-warning gap-1.5"
                    onClick={() => { onResend && onResend(mentor); onClose(); }}>
                    <Send className="w-3.5 h-3.5" /> Resend Invite Email
                  </Button>
                </div>
              )}
              {isReview(mentor.status) && (
                <div className="p-4 rounded-xl bg-info/8 border border-info/20">
                  <p className="text-sm font-semibold text-info mb-1">Under Admin Review</p>
                  <p className="text-xs text-muted-foreground">Mentor submitted onboarding. NexusAI team is verifying their profile.</p>
                </div>
              )}
            </div>
          )}

          {/* ── Programs ── */}
          {detailTab === 'programs' && (
            <div>
              {programs.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-3">
                    <BookOpen className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium text-foreground mb-1">No programs assigned</p>
                  <p className="text-xs text-muted-foreground">Admin assigns programs after mentor verification.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {programs.map((prog, i) => (
                    <ProgramDetailCard key={prog.programId || i} prog={prog} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Batches ── */}
          {detailTab === 'batches' && (
            <div className="space-y-4">
              {analyticsLoading && (
                <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground text-sm">
                  <RefreshCw className="w-4 h-4 animate-spin" /> Loading batch data…
                </div>
              )}
              {analyticsError && !analyticsLoading && (
                <div className="p-4 rounded-xl bg-destructive/8 border border-destructive/20 text-destructive text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" /> {analyticsError}
                  <button onClick={() => { setAnalyticsError(null); setAnalyticsLoading(true); apiGetMentorAnalytics(mentor._id).then(d => setAnalyticsData(d)).catch(e => setAnalyticsError(e.message)).finally(() => setAnalyticsLoading(false)); }} className="ml-auto text-xs underline">Retry</button>
                </div>
              )}
              {!analyticsLoading && !analyticsError && batches.length === 0 && (
                <div className="text-center py-10">
                  <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-3"><Layers className="w-6 h-6 text-muted-foreground" /></div>
                  <p className="text-sm font-medium text-foreground mb-1">No batches yet</p>
                  <p className="text-xs text-muted-foreground">{mentor.name} hasn't created any batches. Batches appear once they're set up on MentorNexus.</p>
                </div>
              )}
              {!analyticsLoading && batches.length > 0 && (
                <>
                  {/* Summary strip */}
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { label: 'Total Batches',   value: summary.totalBatches || 0,        icon: Layers,      color: 'text-primary',   bg: 'bg-primary/8' },
                      { label: 'Active',           value: summary.activeBatches || 0,        icon: CheckCircle, color: 'text-success',   bg: 'bg-success/8' },
                      { label: 'Enrollments',      value: summary.totalEnrollments || 0,     icon: Users,       color: 'text-chart-2',   bg: 'bg-chart-2/10' },
                      { label: 'Support Requests', value: summary.totalTaRequests || 0,      icon: MessageSquare, color: 'text-chart-4', bg: 'bg-chart-4/10' },
                    ].map(k => (
                      <div key={k.label} className={cn('p-2.5 rounded-xl border border-border text-center', k.bg)}>
                        <k.icon className={cn('w-3.5 h-3.5 mx-auto mb-1', k.color)} />
                        <div className="font-bold text-foreground text-sm">{k.value}</div>
                        <div className="text-[10px] text-muted-foreground leading-tight">{k.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Batch cards */}
                  {batches.map(batch => {
                    const bm      = batchStatusMeta(batch.status);
                    const isOpen  = expandedBatch === String(batch._id);
                    const subTab  = bSubTab(String(batch._id));
                    const occ     = batch.stats?.occupancyPct || 0;
                    const occColor= occ >= 90 ? 'bg-destructive' : occ >= 70 ? 'bg-warning' : 'bg-success';

                    return (
                      <div key={String(batch._id)} className="rounded-xl border border-border bg-card overflow-hidden">
                        {/* Batch header */}
                        <div className="p-4">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm font-semibold text-foreground">{batch.batchName}</p>
                                {batch.batchCode && <span className="text-[10px] text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded">{batch.batchCode}</span>}
                              </div>
                              {batch.customLabel && <p className="text-xs text-muted-foreground mt-0.5">"{batch.customLabel}"</p>}
                              <div className="flex flex-wrap gap-1.5 mt-1.5">
                                <span className={cn('text-[11px] px-2 py-0.5 rounded-full border font-medium', bm.cls)}>{bm.label}</span>
                                <span className="text-[11px] px-2 py-0.5 rounded-full border bg-muted text-muted-foreground capitalize">{batch.sessionMode}</span>
                                <span className="text-[11px] px-2 py-0.5 rounded-full border bg-muted text-muted-foreground capitalize">{batch.batchType?.replace('_', ' ')}</span>
                                {(batch.programLabels || []).slice(0, 2).map((pl, i) => (
                                  <span key={i} className="text-[11px] px-2 py-0.5 rounded-full border bg-primary/8 text-primary border-primary/20">{pl}</span>
                                ))}
                              </div>
                            </div>
                            <button onClick={() => setExpandedBatch(isOpen ? null : String(batch._id))}
                              className="flex-shrink-0 p-1.5 rounded-lg hover:bg-muted transition-colors">
                              <ChevronRight className={cn('w-4 h-4 text-muted-foreground transition-transform', isOpen && 'rotate-90')} />
                            </button>
                          </div>

                          {/* Occupancy bar */}
                          <div className="mt-3">
                            <div className="flex justify-between text-[11px] mb-1">
                              <span className="text-muted-foreground">Enrollment</span>
                              <span className="font-semibold text-foreground">{batch.enrolledCount} / {batch.maxStudents} students ({occ}%)</span>
                            </div>
                            <div className="h-2 bg-muted rounded-full overflow-hidden">
                              <div className={cn('h-full rounded-full transition-all', occColor)} style={{ width: `${Math.min(occ, 100)}%` }} />
                            </div>
                          </div>

                          {/* Schedule row */}
                          <div className="grid grid-cols-3 gap-2 mt-3">
                            <div className="text-center p-2 rounded-lg bg-muted/50">
                              <p className="text-[10px] text-muted-foreground">Start</p>
                              <p className="text-xs font-semibold text-foreground">{fmtDate(batch.scheduleConfig?.startDate)}</p>
                            </div>
                            <div className="text-center p-2 rounded-lg bg-muted/50">
                              <p className="text-[10px] text-muted-foreground">End</p>
                              <p className="text-xs font-semibold text-foreground">{fmtDate(batch.scheduleConfig?.endDate)}</p>
                            </div>
                            <div className="text-center p-2 rounded-lg bg-muted/50">
                              <p className="text-[10px] text-muted-foreground">Enrol Deadline</p>
                              <p className="text-xs font-semibold text-foreground">{fmtDate(batch.scheduleConfig?.enrolmentDeadline)}</p>
                            </div>
                          </div>

                          {/* Stats strip */}
                          <div className="flex gap-3 mt-3 text-[11px]">
                            <span className="text-muted-foreground">
                              <span className="font-semibold text-foreground">{batch.stats?.completedSessions}</span>/{batch.stats?.totalSessions} sessions done
                            </span>
                            {batch.stats?.avgAttendancePct != null && (
                              <span className="text-muted-foreground">Avg attendance <span className="font-semibold text-foreground">{batch.stats.avgAttendancePct}%</span></span>
                            )}
                            {batch.stats?.totalWeeks && (
                              <span className="text-muted-foreground"><span className="font-semibold text-foreground">{batch.scheduleConfig?.totalWeeks}</span>w · {batch.scheduleConfig?.hoursPerWeek}h/w</span>
                            )}
                            {batch.stats?.totalTaRequests > 0 && (
                              <span className={cn('font-semibold', batch.stats.pendingTaRequests > 0 ? 'text-warning' : 'text-muted-foreground')}>
                                {batch.stats.pendingTaRequests > 0 ? `${batch.stats.pendingTaRequests} support pending` : `${batch.stats.assignedTaRequests} support assigned`}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Expanded section */}
                        {isOpen && (
                          <div className="border-t border-border">
                            {/* Sub-tabs */}
                            <div className="flex gap-0 border-b border-border bg-muted/20">
                              {[
                                { id: 'sessions',    label: `Sessions (${batch.stats?.totalSessions || 0})` },
                                { id: 'support',     label: `Support Staff (${batch.taRequests?.length || 0})` },
                                { id: 'enrollments', label: `Enrollments` },
                              ].map(st => (
                                <button key={st.id} onClick={() => setBSubTab(String(batch._id), st.id)}
                                  className={cn('px-3 py-2.5 text-[11px] font-semibold border-b-2 transition-colors',
                                    subTab === st.id ? 'border-primary text-primary bg-background' : 'border-transparent text-muted-foreground hover:text-foreground'
                                  )}>
                                  {st.label}
                                </button>
                              ))}
                            </div>

                            <div className="p-4 space-y-3">
                              {/* Sessions sub-tab */}
                              {subTab === 'sessions' && (
                                <>
                                  {batch.stats?.upcomingSessions > 0 && (
                                    <div>
                                      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">Upcoming</p>
                                      <div className="space-y-2">
                                        {(batch.upcomingSessions || []).map((s, i) => (
                                          <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/40 border border-border">
                                            <div className={cn('w-1.5 h-1.5 rounded-full flex-shrink-0 mt-0.5', SESSION_TYPE_COLOR[s.sessionType] || 'text-muted-foreground').replace('text-', 'bg-')} />
                                            <div className="flex-1 min-w-0">
                                              <p className="text-xs font-semibold text-foreground truncate">{s.title}</p>
                                              <p className="text-[11px] text-muted-foreground">
                                                {fmtTimeRange(s.scheduledAt, s.durationMinutes)} · {new Date(s.scheduledAt).toLocaleDateString('en-IN', { day:'numeric', month:'short' })} · W{s.weekNumber}{s.dayNumber ? `/D${s.dayNumber}` : ''}
                                                {s.assignedTaName && <span className="ml-1 text-chart-3">· TA: {s.assignedTaName}</span>}
                                              </p>
                                            </div>
                                            <div className="flex items-center gap-1.5 flex-shrink-0">
                                              <span className={cn('text-[10px] px-1.5 py-0.5 rounded border', SESSION_TYPE_COLOR[s.sessionType] ? 'bg-primary/8 border-primary/20 text-primary' : 'bg-muted border-border text-muted-foreground')}>{s.sessionTypeLabel}</span>
                                              {s.teamsLink && <a href={s.teamsLink} target="_blank" rel="noreferrer" className="text-[10px] text-info underline">Join</a>}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {batch.stats?.completedSessions > 0 && (
                                    <div>
                                      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">Recently Completed</p>
                                      <div className="space-y-2">
                                        {(batch.recentSessions || []).map((s, i) => (
                                          <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/40 border border-border">
                                            <CheckCircle className="w-3.5 h-3.5 text-success flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                              <p className="text-xs font-semibold text-foreground truncate">{s.title}</p>
                                              <p className="text-[11px] text-muted-foreground">{fmtTimeRange(s.scheduledAt, s.durationMinutes)} · {new Date(s.scheduledAt).toLocaleDateString('en-IN',{day:'numeric',month:'short'})} · W{s.weekNumber}</p>
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0 text-[11px] text-muted-foreground">
                                              <span><span className="font-semibold text-foreground">{s.attendanceCount}</span>/{s.totalAttendees} present</span>
                                              {s.recordingUrl && <a href={s.recordingUrl} target="_blank" rel="noreferrer" className="text-info underline text-[10px]">Rec</a>}
                                            </div>
                                          </div>
                                        ))}
                                        {batch.stats.completedSessions > 5 && (
                                          <p className="text-[11px] text-muted-foreground text-center">+{batch.stats.completedSessions - 5} more completed sessions</p>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  {batch.stats?.totalSessions === 0 && (
                                    <p className="text-xs text-muted-foreground text-center py-4">No sessions scheduled yet.</p>
                                  )}
                                </>
                              )}

                              {/* Support Staff sub-tab */}
                              {subTab === 'support' && (
                                <>
                                  {(batch.taRequests || []).length === 0 ? (
                                    <p className="text-xs text-muted-foreground text-center py-4">No support staff requested for this batch.</p>
                                  ) : (batch.taRequests || []).map((ta, i) => {
                                    const tm = TA_STATUS_META[ta.status] || TA_STATUS_META.pending;
                                    return (
                                      <div key={i} className="p-3 rounded-xl border border-border bg-muted/30 space-y-2">
                                        <div className="flex items-center justify-between gap-2">
                                          <div>
                                            <p className="text-xs font-semibold text-foreground">{TA_TYPE_LABEL[ta.requestType] || ta.requestType}</p>
                                            <p className="text-[11px] text-muted-foreground capitalize">{ta.sessionSubtype?.replace(/_/g, ' ') || 'General support'}</p>
                                          </div>
                                          <span className={cn('text-[11px] px-2 py-0.5 rounded-full border font-medium', tm.cls)}>{tm.label}</span>
                                        </div>
                                        <p className="text-xs text-foreground"><span className="text-muted-foreground">Topic: </span>{ta.topic}</p>
                                        {ta.scheduledAt && <p className="text-[11px] text-muted-foreground">Scheduled: {fmtTimeRange(ta.scheduledAt, ta.durationMinutes)} · {new Date(ta.scheduledAt).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}{ta.staffCount > 1 ? ` · ${ta.staffCount} staff` : ''}</p>}
                                        {(ta.assignedTaNames?.length > 0 || ta.assignedTaName) && (
                                          <p className="text-[11px] text-success font-medium">Assigned: {ta.assignedTaNames?.join(', ') || ta.assignedTaName}</p>
                                        )}
                                        {ta.preferredSkills?.length > 0 && (
                                          <div className="flex flex-wrap gap-1">
                                            {ta.preferredSkills.map((sk, j) => <span key={j} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{sk}</span>)}
                                          </div>
                                        )}
                                        {ta.notes && <p className="text-[11px] text-muted-foreground italic">{ta.notes}</p>}
                                      </div>
                                    );
                                  })}
                                </>
                              )}

                              {/* Enrollments sub-tab */}
                              {subTab === 'enrollments' && (
                                <div className="space-y-3">
                                  {/* Enrollment breakdown */}
                                  <div className="grid grid-cols-2 gap-2">
                                    {[
                                      ['Active',          batch.enrollmentSummary?.active || 0,          'text-success'],
                                      ['Consent Pending', batch.enrollmentSummary?.consentPending || 0,  'text-warning'],
                                      ['Shifted Out',     batch.enrollmentSummary?.shifted || 0,         'text-info'],
                                      ['Withdrawn',       batch.enrollmentSummary?.withdrawn || 0,       'text-destructive'],
                                    ].map(([label, count, cls]) => (
                                      <div key={label} className="p-2.5 rounded-lg bg-muted/50 border border-border flex items-center justify-between">
                                        <span className="text-[11px] text-muted-foreground">{label}</span>
                                        <span className={cn('text-sm font-bold', cls)}>{count}</span>
                                      </div>
                                    ))}
                                  </div>
                                  {/* Fee info */}
                                  {batch.feeConfig?.batchType !== 'free' && batch.feeConfig?.totalStudentPrice > 0 && (
                                    <div className="p-3 rounded-xl bg-muted/40 border border-border">
                                      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">Fee Structure</p>
                                      <div className="flex gap-4 text-xs">
                                        <span className="text-muted-foreground">Student Fee: <span className="font-semibold text-foreground">₹{Number(batch.feeConfig.totalStudentPrice).toLocaleString()}</span></span>
                                        <span className="text-muted-foreground">Mentor Payout: <span className="font-semibold text-success">₹{Number(batch.feeConfig.netPayout || 0).toLocaleString()}</span></span>
                                      </div>
                                    </div>
                                  )}
                                  {batch.feeConfig?.batchType === 'free' && (
                                    <div className="p-3 rounded-xl bg-success/8 border border-success/20 text-success text-xs font-medium">Free Batch — No Fee</div>
                                  )}
                                  {/* Venue (offline/hybrid) */}
                                  {batch.venue?.city && (
                                    <div className="p-3 rounded-xl bg-muted/40 border border-border">
                                      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Venue</p>
                                      <p className="text-xs text-foreground">{[batch.venue.street, batch.venue.city, batch.venue.state, batch.venue.pincode].filter(Boolean).join(', ')}</p>
                                      {batch.venue.maps_link && <a href={batch.venue.maps_link} target="_blank" rel="noreferrer" className="text-[11px] text-info underline mt-0.5 block">View on Maps</a>}
                                    </div>
                                  )}
                                  {/* Admin notes */}
                                  {batch.adminReviewNotes && (
                                    <div className="p-3 rounded-xl bg-warning/8 border border-warning/20">
                                      <p className="text-[11px] font-semibold text-warning uppercase tracking-wide mb-1">Admin Note</p>
                                      <p className="text-xs text-foreground">{batch.adminReviewNotes}</p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}

          {/* ── Analytics ── */}
          {detailTab === 'analytics' && (
            <div className="space-y-5">
              {analyticsLoading && (
                <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground text-sm">
                  <RefreshCw className="w-4 h-4 animate-spin" /> Loading analytics…
                </div>
              )}
              {analyticsError && !analyticsLoading && (
                <div className="p-4 rounded-xl bg-destructive/8 border border-destructive/20 text-destructive text-sm">{analyticsError}</div>
              )}
              {!analyticsLoading && !analyticsError && (
                <>
                  {/* Global KPIs */}
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Overview</p>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { label: 'Total Batches',      value: summary.totalBatches || 0,      sub: `${summary.activeBatches || 0} active`,            icon: Layers,        color: 'text-primary',   bg: 'bg-primary/8' },
                        { label: 'Sessions Delivered', value: summary.completedSessions || 0,  sub: `${summary.upcomingSessions || 0} upcoming`,       icon: Calendar,      color: 'text-chart-1',   bg: 'bg-chart-1/10' },
                        { label: 'Total Enrollments',  value: summary.totalEnrollments || 0,   sub: 'active students across batches',                  icon: Users,         color: 'text-chart-2',   bg: 'bg-chart-2/10' },
                        { label: 'Support Requests',   value: summary.totalTaRequests || 0,    sub: `${summary.pendingTaRequests || 0} pending`,       icon: MessageSquare, color: 'text-chart-4',   bg: 'bg-chart-4/10' },
                      ].map(k => (
                        <div key={k.label} className={cn('p-3.5 rounded-xl border border-border', k.bg)}>
                          <div className="flex items-center gap-2 mb-2">
                            <k.icon className={cn('w-4 h-4', k.color)} />
                            <span className="text-[11px] text-muted-foreground font-medium">{k.label}</span>
                          </div>
                          <div className="text-2xl font-bold text-foreground">{k.value}</div>
                          <div className="text-[11px] text-muted-foreground mt-0.5">{k.sub}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Occupancy chart */}
                  {batches.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Batch Occupancy</p>
                      <ResponsiveContainer width="100%" height={160}>
                        <BarChart data={batches.map(b => ({
                          name: b.batchName?.split(' ').slice(0, 2).join(' '),
                          enrolled: b.enrolledCount || 0,
                          remaining: Math.max(0, (b.maxStudents || 0) - (b.enrolledCount || 0)),
                          capacity: b.maxStudents || 0,
                        }))} margin={{ top: 4, right: 4, left: -20, bottom: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                          <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                          <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 11 }} />
                          <Bar dataKey="enrolled" name="Enrolled" stackId="a" fill="hsl(var(--chart-2))" radius={[0, 0, 0, 0]} />
                          <Bar dataKey="remaining" name="Remaining" stackId="a" fill="hsl(var(--muted))" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Sessions breakdown chart */}
                  {batches.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Sessions Breakdown per Batch</p>
                      <ResponsiveContainer width="100%" height={160}>
                        <BarChart data={batches.map(b => ({
                          name: b.batchName?.split(' ').slice(0, 2).join(' '),
                          completed: b.stats?.completedSessions || 0,
                          upcoming: b.stats?.upcomingSessions || 0,
                          cancelled: b.stats?.cancelledSessions || 0,
                        }))} margin={{ top: 4, right: 4, left: -20, bottom: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                          <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                          <YAxis tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                          <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 11 }} />
                          <Bar dataKey="completed" name="Completed" stackId="b" fill="hsl(var(--chart-2))" />
                          <Bar dataKey="upcoming"  name="Upcoming"  stackId="b" fill="hsl(var(--primary))" />
                          <Bar dataKey="cancelled" name="Cancelled" stackId="b" fill="hsl(var(--destructive))" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {/* Support requests breakdown */}
                  {summary.totalTaRequests > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Support Requests by Batch</p>
                      <div className="space-y-2">
                        {batches.filter(b => b.taRequests?.length > 0).map(b => (
                          <div key={String(b._id)} className="p-3 rounded-xl border border-border bg-muted/30 flex items-center justify-between gap-3">
                            <div>
                              <p className="text-xs font-semibold text-foreground">{b.batchName}</p>
                              <p className="text-[11px] text-muted-foreground">{b.taRequests.length} request{b.taRequests.length !== 1 ? 's' : ''} · {b.taRequests.filter(t => t.requestType === 'ita').length} ITA · {b.taRequests.filter(t => t.requestType === 'sme').length} SME</p>
                            </div>
                            <div className="flex gap-1.5">
                              {b.stats?.pendingTaRequests > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full border bg-warning/10 text-warning border-warning/25 font-medium">{b.stats.pendingTaRequests} pending</span>}
                              {b.stats?.assignedTaRequests > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full border bg-success/10 text-success border-success/25 font-medium">{b.stats.assignedTaRequests} assigned</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {batches.length === 0 && (
                    <div className="text-center py-8 text-sm text-muted-foreground">No batch data available to analyse.</div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Profile Eval ── */}
          {detailTab === 'eval' && (
            <div className="space-y-4">
              {!evalReport && !aiR?.report_1?.reasoning ? (
                <div className="text-center py-10">
                  <div className="w-12 h-12 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-3">
                    <Sparkles className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium text-foreground mb-1">No evaluation yet</p>
                  <p className="text-xs text-muted-foreground">MentorEval report will appear after onboarding is submitted.</p>
                </div>
              ) : (
                <>
                  {/* MentorEval decision badge */}
                  {mentor.mentorevalDecision && (() => {
                    const manualOverride = isManualOverride;

                    if (manualOverride) {
                      return (
                        <div className="p-4 rounded-xl border bg-success/8 border-success/25 text-success font-medium text-sm space-y-1">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 flex-shrink-0" />
                            <span>Manually Approved by NexusAI Admin</span>
                          </div>
                          <p className="text-xs text-muted-foreground font-normal pl-6">
                            MentorEval flagged areas for improvement, but the admin reviewed this profile and granted approval. The feedback below is provided as development guidance only.
                          </p>
                        </div>
                      );
                    }

                    return (
                      <div className={cn('p-4 rounded-xl border font-medium text-sm flex items-center gap-2',
                        mentor.mentorevalDecision === 'ACCEPTED'               ? 'bg-success/8 border-success/25 text-success' :
                        mentor.mentorevalDecision === 'CONDITIONALLY_ACCEPTED' ? 'bg-warning/8 border-warning/25 text-warning' :
                                                                                 'bg-destructive/8 border-destructive/25 text-destructive'
                      )}>
                        {mentor.mentorevalDecision === 'ACCEPTED' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                        MentorEval Decision: {mentor.mentorevalDecision.replace(/_/g, ' ')}
                      </div>
                    );
                  })()}

                  {/* Full mentoreval report */}
                  {evalReport && (
                    <>
                      {evalReport.mentor_message && (
                        <div>
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Personal Message from MentorEval</p>
                          <p className="text-sm text-foreground leading-relaxed p-4 bg-primary/5 border border-primary/15 rounded-xl">{evalReport.mentor_message}</p>
                        </div>
                      )}
                      {evalReport.strengths?.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-success uppercase tracking-wide mb-2">Strengths</p>
                          <ul className="space-y-1.5">
                            {evalReport.strengths.map((s, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                                <CheckCircle className="w-3.5 h-3.5 text-success mt-0.5 flex-shrink-0" />{s}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {evalReport.improvements?.length > 0 && (
                        <div>
                          <p className={cn('text-xs font-semibold uppercase tracking-wide mb-2', isManualOverride ? 'text-muted-foreground' : 'text-warning')}>
                            {isManualOverride ? 'Development Feedback' : 'Areas to Improve'}
                          </p>
                          <ul className="space-y-1.5">
                            {evalReport.improvements.map((s, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                                {isManualOverride
                                  ? <Lightbulb className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                                  : <AlertCircle className="w-3.5 h-3.5 text-warning mt-0.5 flex-shrink-0" />
                                }
                                {s}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {evalReport.program_suggestions?.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-2">Suggested Programs</p>
                          <div className="flex flex-wrap gap-1.5">
                            {evalReport.program_suggestions.map((p, i) => <span key={i} className="badge-primary text-xs">{p}</span>)}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* AI Reports (3 passes) */}
                  {aiR?.report_1?.reasoning && (
                    <div className="p-4 rounded-xl border border-border bg-muted/30">
                      <p className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5">
                        <TrendingUp className="w-3.5 h-3.5 text-primary" /> Acceptance Recommendation
                      </p>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium', aiR.report_1.recommended ? 'badge-success' : 'badge-destructive')}>
                          {aiR.report_1.recommended ? 'Recommended' : 'Not Recommended'}
                        </span>
                        {aiR.report_1.confidence_score != null && (
                          <span className="text-xs text-muted-foreground">Confidence: {Math.round(aiR.report_1.confidence_score * 100)}%</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{aiR.report_1.reasoning}</p>
                    </div>
                  )}
                  {aiR?.report_2?.consistency_summary && (
                    <div className="p-4 rounded-xl border border-border bg-muted/30">
                      <p className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5">
                        <Shield className="w-3.5 h-3.5 text-chart-3" /> Profile & Video Alignment
                      </p>
                      {aiR.report_2.alignment_score != null && (
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-primary rounded-full" style={{ width: `${aiR.report_2.alignment_score}%` }} />
                          </div>
                          <span className="text-xs font-semibold text-foreground">{aiR.report_2.alignment_score}%</span>
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground mb-2">{aiR.report_2.consistency_summary}</p>
                      {aiR.report_2.strengths?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {aiR.report_2.strengths.map((s, i) => <span key={i} className="text-[11px] px-1.5 py-0.5 rounded bg-success/10 text-success">{s}</span>)}
                        </div>
                      )}
                    </div>
                  )}
                  {aiR?.report_3?.verdict && (
                    <div className="p-4 rounded-xl border border-border bg-muted/30">
                      <p className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5">
                        <Award className="w-3.5 h-3.5 text-chart-4" /> Mentor Potential Score
                      </p>
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        {[
                          ['Communication',      aiR.report_3.communication],
                          ['Confidence',         aiR.report_3.confidence],
                          ['Clarity',            aiR.report_3.clarity],
                          ['Teaching Potential', aiR.report_3.teaching_potential],
                        ].filter(([, v]) => v != null).map(([label, val]) => (
                          <div key={label}>
                            <div className="flex justify-between text-[11px] mb-0.5">
                              <span className="text-muted-foreground">{label}</span>
                              <span className="font-semibold text-foreground">{val}/10</span>
                            </div>
                            <div className="h-1 bg-muted rounded-full overflow-hidden">
                              <div className="h-full bg-primary rounded-full" style={{ width: `${val * 10}%` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                      {aiR.report_3.coaching_notes && (
                        <p className="text-xs text-muted-foreground leading-relaxed mt-2">{aiR.report_3.coaching_notes}</p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ── Calendar helpers ──────────────────────────────────────────────────────────
const CAL_MONTHS   = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const CAL_DAYS     = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const calDateKey   = (d) => new Date(d).toISOString().slice(0, 10);
const isSameCalDay = (a, b) => calDateKey(a) === calDateKey(b);
const isCalToday   = (d) => isSameCalDay(d, new Date());

const SESSION_CAL_COLORS = {
  live:     { bg: 'bg-primary',           chip: 'bg-primary/12 text-primary border-primary/25' },
  doubt:    { bg: 'bg-warning',           chip: 'bg-warning/12 text-warning border-warning/25' },
  support:  { bg: 'bg-chart-3',           chip: 'bg-chart-3/12 text-chart-3 border-chart-3/25' },
  adhoc:    { bg: 'bg-chart-4',           chip: 'bg-chart-4/12 text-chart-4 border-chart-4/25' },
  recorded: { bg: 'bg-muted-foreground',  chip: 'bg-muted text-muted-foreground border-border' },
};
const calColor = (type) => SESSION_CAL_COLORS[type] || SESSION_CAL_COLORS.live;
const SESSION_STATUS_DOT = { live: 'bg-success animate-pulse', upcoming: 'bg-primary', completed: 'bg-muted-foreground', cancelled: 'bg-destructive' };

function buildMonthGrid(year, month) {
  const firstDow  = (new Date(year, month, 1).getDay() + 6) % 7; // Mon=0
  const daysInMon = new Date(year, month + 1, 0).getDate();
  const grid = [];
  for (let i = firstDow - 1; i >= 0; i--) grid.push({ date: new Date(year, month, -i), cur: false });
  for (let d = 1; d <= daysInMon; d++) grid.push({ date: new Date(year, month, d), cur: true });
  while (grid.length % 7 !== 0) grid.push({ date: new Date(year, month + 1, grid.length - firstDow - daysInMon + 1), cur: false });
  while (grid.length < 35)      grid.push({ date: new Date(year, month + 1, grid.length - firstDow - daysInMon + 1), cur: false });
  return grid;
}
function buildWeekDays(pivot) {
  const d = new Date(pivot); const dow = (d.getDay() + 6) % 7; d.setDate(d.getDate() - dow);
  return Array.from({ length: 7 },  (_, i) => { const x = new Date(d); x.setDate(d.getDate() + i); return x; });
}
function build15Days(pivot) {
  const d = new Date(pivot);
  return Array.from({ length: 15 }, (_, i) => { const x = new Date(d); x.setDate(d.getDate() + i); return x; });
}

// ---- Calendar Day Cell ----
const CalDayCell = ({ date, cur, daySessions, selected, onSelect, compact }) => {
  const isTod = isCalToday(date);
  const hasSess = daySessions.length > 0;
  return (
    <button onClick={() => onSelect(date)}
      className={cn(
        'relative text-left rounded-xl border transition-all w-full',
        compact ? 'h-10 p-1' : 'min-h-[88px] p-2',
        selected  ? 'border-primary/50 bg-primary/8 ring-1 ring-primary/30' : 'border-border hover:bg-muted/40',
        !cur      && 'opacity-35',
      )}>
      <span className={cn(
        'flex items-center justify-center font-semibold rounded-full',
        compact ? 'w-5 h-5 text-[10px] mx-auto' : 'w-6 h-6 text-xs mb-1',
        isTod ? 'bg-primary text-primary-foreground' : 'text-foreground',
      )}>{date.getDate()}</span>
      {!compact && hasSess && (
        <div className="space-y-0.5">
          {daySessions.slice(0, 3).map((s, i) => (
            <div key={i} className={cn('text-[10px] truncate rounded px-1 font-medium border', calColor(s.sessionType).chip)}>
              {fmtTime(s.scheduledAt)}–{fmtTime(new Date(new Date(s.scheduledAt).getTime()+(s.durationMinutes||60)*60000))} {s.title}
            </div>
          ))}
          {daySessions.length > 3 && <p className="text-[10px] text-muted-foreground pl-1">+{daySessions.length - 3}</p>}
        </div>
      )}
      {compact && hasSess && (
        <div className="flex gap-0.5 justify-center flex-wrap">
          {daySessions.slice(0, 3).map((s, i) => <div key={i} className={cn('w-1.5 h-1.5 rounded-full', calColor(s.sessionType).bg)} />)}
        </div>
      )}
    </button>
  );
};

// ---- Session Detail Card ----
const SessionDetailCard = ({ session: s, selected, onSelect }) => {
  const cc = calColor(s.sessionType);
  return (
    <div className={cn('rounded-xl border transition-all cursor-pointer overflow-hidden',
      selected ? `${cc.chip} border-opacity-100` : 'bg-card border-border hover:bg-muted/30'
    )} onClick={onSelect}>
      <div className="p-3 flex gap-3 items-start">
        <div className="w-[4.5rem] flex-shrink-0 text-right pt-0.5">
          <p className="text-xs font-bold text-foreground leading-tight">{fmtTime(s.scheduledAt)}</p>
          <p className="text-[10px] text-foreground/70 font-medium">– {fmtTime(new Date(new Date(s.scheduledAt).getTime() + (s.durationMinutes||60)*60000))}</p>
          <p className="text-[10px] text-muted-foreground">{minsToHours(s.durationMinutes||60)}</p>
        </div>
        <div className={cn('w-1 self-stretch rounded-full flex-shrink-0', cc.bg)} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-xs font-semibold text-foreground">{s.title}</p>
            <div className={cn('w-1.5 h-1.5 rounded-full', SESSION_STATUS_DOT[s.status] || 'bg-muted-foreground')} />
            <span className={cn('text-[10px] px-1.5 py-0.5 rounded border font-medium', cc.chip)}>{s.sessionTypeLabel}</span>
            {s.status === 'cancelled' && <span className="text-[10px] text-destructive font-medium">Cancelled</span>}
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{s.batchName}{s.weekNumber ? ` · W${s.weekNumber}${s.dayNumber ? `/D${s.dayNumber}` : ''}` : ''}</p>
          {s.programLabels?.length > 0 && <p className="text-[10px] text-muted-foreground truncate">{s.programLabels.join(', ')}</p>}
        </div>
        <ChevronRight className={cn('w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5 transition-transform', selected && 'rotate-90')} />
      </div>
      {selected && (
        <div className="px-4 pb-4 pt-3 border-t border-border/50 space-y-3 bg-background/60">
          {s.description && (
            <div>
              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Agenda / Description</p>
              <p className="text-xs text-foreground leading-relaxed bg-muted/40 rounded-lg px-3 py-2">{s.description}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px]">
            {[
              ['Mode',    s.mode || '—'],
              ['Host',    s.hostTypeLabel],
              ['Status',  s.status],
              ['Pattern', s.pattern || 'single'],
              ['Week',    s.weekNumber ? `Week ${s.weekNumber}${s.dayNumber ? `, Day ${s.dayNumber}` : ''}` : '—'],
            ].map(([k, v]) => (
              <div key={k}><span className="text-muted-foreground">{k}: </span><span className="font-semibold text-foreground capitalize">{v}</span></div>
            ))}
          </div>
          {s.assignedTaName && (
            <div className="flex items-center gap-1.5 text-[11px] text-chart-3 font-medium">
              <Users className="w-3 h-3" /> Support: {s.assignedTaName}
            </div>
          )}
          {s.status === 'completed' && (
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <CheckCircle className="w-3 h-3 text-success" />
              Attendance: <span className="font-semibold text-foreground">{s.attendanceCount}/{s.totalAttendees}</span> present
              {s.totalAttendees > 0 && <span className="text-success font-medium">({Math.round((s.attendanceCount/s.totalAttendees)*100)}%)</span>}
            </div>
          )}
          {s.cancelledReason && <p className="text-[11px] text-destructive bg-destructive/8 rounded px-2 py-1">Cancelled: {s.cancelledReason}</p>}
          <div className="flex gap-3 flex-wrap">
            {s.teamsLink && (
              <a href={s.teamsLink} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-[11px] text-info font-medium hover:underline border border-info/25 bg-info/8 px-2 py-1 rounded-lg">
                <ArrowUpRight className="w-3 h-3" /> Join Teams Meeting
              </a>
            )}
            {s.recordingUrl && (
              <a href={s.recordingUrl} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-[11px] text-primary font-medium hover:underline border border-primary/25 bg-primary/8 px-2 py-1 rounded-lg">
                <ArrowUpRight className="w-3 h-3" /> View Recording
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ---- Mentor Calendar Modal ----
const MentorCalendarModal = ({ mentor, open, onClose }) => {
  const [view,            setView]            = useState('month');
  const [pivotDate,       setPivotDate]       = useState(() => new Date());
  const [selectedDate,    setSelectedDate]    = useState(() => new Date());
  const [selectedSession, setSelectedSession] = useState(null);
  const [allSessions,     setAllSessions]     = useState([]);
  const [calBatches,      setCalBatches]      = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [calError,        setCalError]        = useState(null);

  useEffect(() => {
    if (!open || !mentor?._id) return;
    setAllSessions([]); setCalBatches([]); setCalError(null); setSelectedSession(null);
    setLoading(true);
    const from = new Date(); from.setMonth(from.getMonth() - 2);
    const to   = new Date(); to.setMonth(to.getMonth() + 5);
    apiGetMentorCalendar(mentor._id, from.toISOString(), to.toISOString())
      .then(d => { setAllSessions(d.sessions || []); setCalBatches(d.batches || []); })
      .catch(e => setCalError(e.message))
      .finally(() => setLoading(false));
  }, [open, mentor?._id]);

  if (!mentor) return null;

  const sessionsOn = (d) => allSessions.filter(s => calDateKey(s.scheduledAt) === calDateKey(d));
  const todaySess  = sessionsOn(new Date()).sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
  const selSess    = sessionsOn(selectedDate).sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));
  const selDayMins = selSess.reduce((t, s) => t + (s.durationMinutes || 60), 0);
  const todayMins  = todaySess.reduce((t, s) => t + (s.durationMinutes || 60), 0);

  const navigate = (dir) => {
    const d = new Date(pivotDate);
    const delta = { week: 7, '15day': 15 };
    if (delta[view]) d.setDate(d.getDate() + dir * delta[view]);
    else d.setMonth(d.getMonth() + dir * (view === '3month' ? 3 : 1));
    setPivotDate(d);
  };

  const navTitle = () => {
    if (view === 'today')  return `Today — ${new Date().toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}`;
    if (view === 'month')  return `${CAL_MONTHS[pivotDate.getMonth()]} ${pivotDate.getFullYear()}`;
    if (view === '3month') { const e = new Date(pivotDate.getFullYear(), pivotDate.getMonth()+2, 1); return `${CAL_MONTHS[pivotDate.getMonth()]} – ${CAL_MONTHS[e.getMonth()]} ${pivotDate.getFullYear()}`; }
    if (view === 'week')   { const w = buildWeekDays(pivotDate); return `${fmtDate(w[0])} – ${fmtDate(w[6])}`; }
    if (view === '15day')  { const d = build15Days(pivotDate);   return `${fmtDate(d[0])} – ${fmtDate(d[14])}`; }
    return '';
  };

  const engagementStats = [
    { label: 'This Week',     n: allSessions.filter(s => { const w = buildWeekDays(new Date()); return new Date(s.scheduledAt) >= w[0] && new Date(s.scheduledAt) <= w[6]; }).length },
    { label: 'This Month',    n: allSessions.filter(s => { const d = new Date(s.scheduledAt); return d.getMonth() === new Date().getMonth() && d.getFullYear() === new Date().getFullYear(); }).length },
    { label: 'Next 15 Days',  n: allSessions.filter(s => { const d = new Date(s.scheduledAt), now = new Date(), f = new Date(); f.setDate(now.getDate()+15); return d >= now && d <= f; }).length },
    { label: 'Next 3 Months', n: allSessions.filter(s => { const d = new Date(s.scheduledAt), now = new Date(), f = new Date(); f.setMonth(now.getMonth()+3); return d >= now && d <= f; }).length },
    { label: 'Completed',     n: allSessions.filter(s => s.status === 'completed').length },
    { label: 'Cancelled',     n: allSessions.filter(s => s.status === 'cancelled').length },
  ];

  const SelDatePanel = () => selSess.length > 0 ? (
    <div className="mt-5 border-t border-border pt-5">
      {/* Selected-day hero */}
      <div className="rounded-2xl bg-gradient-to-br from-primary/10 via-primary/4 to-transparent border border-primary/20 p-4 mb-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[11px] font-semibold text-primary uppercase tracking-wide mb-0.5">Selected Day</p>
            <p className="text-base font-bold text-foreground leading-tight">
              {selectedDate.toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}
            </p>
          </div>
          {selSess.filter(s => s.status === 'live').length > 0 && (
            <span className="flex items-center gap-1.5 text-xs text-success font-bold px-2.5 py-1 bg-success/10 border border-success/25 rounded-lg">
              <div className="w-2 h-2 bg-success rounded-full animate-pulse" /> Live Now
            </span>
          )}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <div className="flex items-center gap-1.5 bg-background/70 border border-border/60 rounded-lg px-3 py-1.5">
            <Calendar className="w-3.5 h-3.5 text-primary" />
            <span className="text-xs font-bold text-foreground">{selSess.length}</span>
            <span className="text-xs text-muted-foreground">session{selSess.length > 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center gap-1.5 bg-background/70 border border-border/60 rounded-lg px-3 py-1.5">
            <Clock className="w-3.5 h-3.5 text-primary" />
            <span className="text-xs font-bold text-foreground">{minsToHours(selDayMins)}</span>
            <span className="text-xs text-muted-foreground">total</span>
          </div>
          {selSess.filter(s => s.status === 'completed').length > 0 && (
            <div className="flex items-center gap-1.5 bg-success/8 border border-success/20 rounded-lg px-3 py-1.5">
              <CheckCircle className="w-3.5 h-3.5 text-success" />
              <span className="text-xs font-bold text-success">{selSess.filter(s => s.status === 'completed').length}</span>
              <span className="text-xs text-muted-foreground">done</span>
            </div>
          )}
          {selSess.filter(s => s.status === 'upcoming').length > 0 && (
            <div className="flex items-center gap-1.5 bg-warning/8 border border-warning/20 rounded-lg px-3 py-1.5">
              <Clock className="w-3.5 h-3.5 text-warning" />
              <span className="text-xs font-bold text-warning">{selSess.filter(s => s.status === 'upcoming').length}</span>
              <span className="text-xs text-muted-foreground">upcoming</span>
            </div>
          )}
        </div>
      </div>
      <div className="space-y-2">
        {selSess.map(s => (
          <SessionDetailCard key={String(s._id)} session={s}
            selected={selectedSession?._id === s._id}
            onSelect={() => setSelectedSession(p => p?._id === s._id ? null : s)} />
        ))}
      </div>
    </div>
  ) : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[96vw] w-full h-[95vh] p-0 flex flex-col overflow-hidden gap-0">

        {/* ── Top bar ── */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-card flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full gradient-primary flex items-center justify-center text-xs font-bold text-primary-foreground flex-shrink-0">
              {mentor.name.split(' ').slice(0,2).map(n=>n[0]).join('')}
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">{mentor.name} — Calendar</p>
              <p className="text-xs text-muted-foreground">{allSessions.length} sessions fetched · {calBatches.length} batches</p>
            </div>
          </div>
          <div className="flex items-center gap-1 bg-muted/60 rounded-xl p-1">
            {[{id:'today',l:'Today'},{id:'week',l:'Week'},{id:'15day',l:'15 Days'},{id:'month',l:'Month'},{id:'3month',l:'3 Months'}].map(v => (
              <button key={v.id} onClick={() => { setView(v.id); if(v.id==='today') setSelectedDate(new Date()); }}
                className={cn('px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors whitespace-nowrap',
                  view===v.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                )}>{v.l}</button>
            ))}
          </div>
        </div>

        {/* ── Nav strip ── */}
        {view !== 'today' && (
          <div className="flex items-center justify-between px-5 py-2 border-b border-border/60 bg-muted/15 flex-shrink-0">
            <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
              <ChevronRight className="w-4 h-4 rotate-180 text-muted-foreground" />
            </button>
            <p className="text-sm font-semibold text-foreground">{navTitle()}</p>
            <div className="flex items-center gap-2">
              <button onClick={() => { setPivotDate(new Date()); setSelectedDate(new Date()); }} className="text-xs text-primary font-medium px-2.5 py-1 rounded-lg hover:bg-primary/10 border border-primary/20">Today</button>
              <button onClick={() => navigate(1)} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
          </div>
        )}

        {/* ── Content ── */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center gap-2 text-muted-foreground">
            <RefreshCw className="w-5 h-5 animate-spin" /> Loading calendar data…
          </div>
        ) : calError ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="p-5 bg-destructive/8 border border-destructive/20 rounded-xl text-destructive text-sm max-w-sm text-center">{calError}</div>
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex">

            {/* ── Main calendar area ── */}
            <div className="flex-1 overflow-y-auto">

              {/* TODAY */}
              {view === 'today' && (
                <div className="p-5 space-y-4 max-w-3xl mx-auto">
                  <div className="rounded-2xl bg-gradient-to-br from-primary/12 via-primary/5 to-transparent border border-primary/20 p-5">
                    <div className="flex items-start justify-between flex-wrap gap-3">
                      <div>
                        <p className="text-[11px] font-semibold text-primary uppercase tracking-wide mb-1">Today's Schedule</p>
                        <p className="text-xl font-bold text-foreground">{new Date().toLocaleDateString('en-IN', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {todaySess.length === 0 ? 'No sessions scheduled' : `${todaySess.length} session${todaySess.length>1?'s':''} · ${todaySess.filter(s=>s.status==='completed').length} completed · ${todaySess.filter(s=>s.status==='upcoming').length} upcoming`}
                        </p>
                      </div>
                      {todaySess.filter(s=>s.status==='live').length > 0 && (
                        <span className="flex items-center gap-1.5 text-xs text-success font-bold px-3 py-1.5 bg-success/10 border border-success/25 rounded-lg">
                          <div className="w-2 h-2 bg-success rounded-full animate-pulse" /> Live Now
                        </span>
                      )}
                    </div>
                    {todaySess.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        <div className="flex items-center gap-1.5 bg-background/70 border border-border/60 rounded-lg px-3 py-1.5">
                          <Calendar className="w-3.5 h-3.5 text-primary" />
                          <span className="text-sm font-bold text-foreground">{todaySess.length}</span>
                          <span className="text-xs text-muted-foreground">session{todaySess.length > 1 ? 's' : ''}</span>
                        </div>
                        <div className="flex items-center gap-1.5 bg-background/70 border border-border/60 rounded-lg px-3 py-1.5">
                          <Clock className="w-3.5 h-3.5 text-primary" />
                          <span className="text-sm font-bold text-foreground">{minsToHours(todayMins)}</span>
                          <span className="text-xs text-muted-foreground">total hours</span>
                        </div>
                        {todaySess.filter(s => s.status === 'completed').length > 0 && (
                          <div className="flex items-center gap-1.5 bg-success/8 border border-success/20 rounded-lg px-3 py-1.5">
                            <CheckCircle className="w-3.5 h-3.5 text-success" />
                            <span className="text-sm font-bold text-success">{todaySess.filter(s => s.status === 'completed').length}</span>
                            <span className="text-xs text-muted-foreground">completed</span>
                          </div>
                        )}
                        {todaySess.filter(s => s.status === 'upcoming').length > 0 && (
                          <div className="flex items-center gap-1.5 bg-warning/8 border border-warning/20 rounded-lg px-3 py-1.5">
                            <Clock className="w-3.5 h-3.5 text-warning" />
                            <span className="text-sm font-bold text-warning">{todaySess.filter(s => s.status === 'upcoming').length}</span>
                            <span className="text-xs text-muted-foreground">upcoming</span>
                          </div>
                        )}
                        {todaySess.filter(s => s.status === 'cancelled').length > 0 && (
                          <div className="flex items-center gap-1.5 bg-destructive/8 border border-destructive/20 rounded-lg px-3 py-1.5">
                            <XCircle className="w-3.5 h-3.5 text-destructive" />
                            <span className="text-sm font-bold text-destructive">{todaySess.filter(s => s.status === 'cancelled').length}</span>
                            <span className="text-xs text-muted-foreground">cancelled</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {todaySess.length === 0 ? (
                    <div className="text-center py-10">
                      <Calendar className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-40" />
                      <p className="text-sm font-medium text-foreground">Free day</p>
                      <p className="text-xs text-muted-foreground">{mentor.name} has no sessions scheduled today</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {todaySess.map(s => (
                        <SessionDetailCard key={String(s._id)} session={s}
                          selected={selectedSession?._id === s._id}
                          onSelect={() => setSelectedSession(p => p?._id === s._id ? null : s)} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* MONTH */}
              {view === 'month' && (
                <div className="p-4">
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {CAL_DAYS.map(d => <div key={d} className="text-center text-[11px] font-semibold text-muted-foreground py-1.5">{d}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {buildMonthGrid(pivotDate.getFullYear(), pivotDate.getMonth()).map((cell, i) => (
                      <CalDayCell key={i} {...cell} daySessions={sessionsOn(cell.date)}
                        selected={isSameCalDay(cell.date, selectedDate)}
                        onSelect={(d) => { setSelectedDate(d); setSelectedSession(null); }} />
                    ))}
                  </div>
                  <SelDatePanel />
                </div>
              )}

              {/* WEEK */}
              {view === 'week' && (
                <div className="p-4">
                  <div className="grid grid-cols-7 gap-2">
                    {buildWeekDays(pivotDate).map((day, i) => {
                      const ds = sessionsOn(day);
                      const isSel = isSameCalDay(day, selectedDate);
                      const isTod = isCalToday(day);
                      return (
                        <div key={i} onClick={() => { setSelectedDate(day); setSelectedSession(null); }}
                          className={cn('rounded-xl border p-2 min-h-[140px] cursor-pointer transition-all', isSel ? 'border-primary/50 ring-1 ring-primary/30 bg-primary/5' : 'border-border hover:bg-muted/30', isTod && !isSel && 'bg-primary/5 border-primary/20')}>
                          <div className="text-center mb-2">
                            <p className="text-[11px] text-muted-foreground font-medium">{CAL_DAYS[i]}</p>
                            <p className={cn('text-xl font-bold leading-none mt-0.5', isTod ? 'text-primary' : 'text-foreground')}>{day.getDate()}</p>
                            {ds.length > 0 && <p className="text-[10px] text-muted-foreground">{ds.length} session{ds.length>1?'s':''}</p>}
                          </div>
                          <div className="space-y-1">
                            {ds.map((s, j) => (
                              <div key={j} className={cn('text-[10px] rounded-lg px-1.5 py-0.5 truncate font-medium border', calColor(s.sessionType).chip)}>
                                {fmtTime(s.scheduledAt)}–{fmtTime(new Date(new Date(s.scheduledAt).getTime()+(s.durationMinutes||60)*60000))} {s.title}
                              </div>
                            ))}
                            {ds.length === 0 && <p className="text-[10px] text-muted-foreground text-center mt-4">—</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <SelDatePanel />
                </div>
              )}

              {/* 15 DAY */}
              {view === '15day' && (
                <div className="p-4">
                  <div className="grid grid-cols-5 gap-2">
                    {build15Days(pivotDate).map((day, i) => {
                      const ds = sessionsOn(day);
                      const isSel = isSameCalDay(day, selectedDate);
                      const isTod = isCalToday(day);
                      return (
                        <div key={i} onClick={() => { setSelectedDate(day); setSelectedSession(null); }}
                          className={cn('rounded-xl border p-2.5 min-h-[120px] cursor-pointer transition-all', isSel ? 'border-primary/50 ring-1 ring-primary/30 bg-primary/5' : 'border-border hover:bg-muted/30', isTod && !isSel && 'bg-primary/5 border-primary/20')}>
                          <div className="mb-2">
                            <p className="text-[10px] text-muted-foreground">{day.toLocaleDateString('en-IN',{weekday:'short'})}</p>
                            <p className={cn('text-2xl font-bold leading-tight', isTod ? 'text-primary' : 'text-foreground')}>{day.getDate()}</p>
                            <p className="text-[10px] text-muted-foreground">{day.toLocaleDateString('en-IN',{month:'short'})}</p>
                          </div>
                          <div className="space-y-0.5">
                            {ds.map((s, j) => (
                              <div key={j} className={cn('text-[10px] rounded px-1 py-0.5 truncate font-medium border', calColor(s.sessionType).chip)}>
                                {fmtTime(s.scheduledAt)}–{fmtTime(new Date(new Date(s.scheduledAt).getTime()+(s.durationMinutes||60)*60000))} {s.title}
                              </div>
                            ))}
                            {ds.length === 0 && <p className="text-[10px] text-muted-foreground mt-2">—</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <SelDatePanel />
                </div>
              )}

              {/* 3 MONTHS */}
              {view === '3month' && (
                <div className="p-4 space-y-6">
                  <div className="grid grid-cols-3 gap-6">
                    {[0,1,2].map(offset => {
                      const y = new Date(pivotDate.getFullYear(), pivotDate.getMonth()+offset, 1);
                      const grid = buildMonthGrid(y.getFullYear(), y.getMonth());
                      return (
                        <div key={offset}>
                          <p className="text-sm font-bold text-foreground text-center mb-3">{CAL_MONTHS[y.getMonth()]} {y.getFullYear()}</p>
                          <div className="grid grid-cols-7 gap-0.5 mb-1">
                            {CAL_DAYS.map(d => <div key={d} className="text-center text-[9px] font-medium text-muted-foreground">{d[0]}</div>)}
                          </div>
                          <div className="grid grid-cols-7 gap-0.5">
                            {grid.map((cell, i) => (
                              <CalDayCell key={i} {...cell} daySessions={sessionsOn(cell.date)} compact
                                selected={isSameCalDay(cell.date, selectedDate)}
                                onSelect={(d) => { setSelectedDate(d); setSelectedSession(null); }} />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <SelDatePanel />
                </div>
              )}
            </div>

            {/* ── Right sidebar ── */}
            <div className="w-60 flex-shrink-0 border-l border-border overflow-y-auto bg-card/50">
              <div className="p-4 space-y-5">

                {/* Engagement stats */}
                <div>
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">Engagement</p>
                  <div className="space-y-0">
                    {engagementStats.map(e => (
                      <div key={e.label} className="flex justify-between items-center py-2 border-b border-border/40 last:border-0">
                        <span className="text-[11px] text-muted-foreground">{e.label}</span>
                        <span className={cn('text-xs font-bold', e.n > 0 ? 'text-foreground' : 'text-muted-foreground')}>{e.n}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Batch summary */}
                {calBatches.length > 0 && (
                  <div>
                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">Batches ({calBatches.length})</p>
                    <div className="space-y-2">
                      {calBatches.map(b => (
                        <div key={String(b._id)} className="p-2.5 rounded-xl bg-muted/40 border border-border">
                          <p className="text-[11px] font-semibold text-foreground leading-tight">{b.batchName}</p>
                          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                            <span className={cn('text-[10px] px-1.5 py-0.5 rounded-full border font-medium', batchStatusMeta(b.status).cls)}>{batchStatusMeta(b.status).label}</span>
                            <span className="text-[10px] text-muted-foreground capitalize">{b.sessionMode}</span>
                          </div>
                          {b.scheduleConfig?.startDate && (
                            <p className="text-[10px] text-muted-foreground mt-1">{fmtDate(b.scheduleConfig.startDate)} → {fmtDate(b.scheduleConfig.endDate)}</p>
                          )}
                          <div className="mt-1.5">
                            <div className="flex justify-between text-[10px] mb-0.5">
                              <span className="text-muted-foreground">Enrolled</span>
                              <span className="font-semibold text-foreground">{b.enrolledCount}/{b.maxStudents}</span>
                            </div>
                            <div className="h-1 bg-muted rounded-full overflow-hidden">
                              <div className="h-full bg-primary rounded-full" style={{ width: `${b.maxStudents > 0 ? Math.min((b.enrolledCount/b.maxStudents)*100, 100) : 0}%` }} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Legend */}
                <div>
                  <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">Legend</p>
                  <div className="space-y-1.5">
                    {[['live','Live Session'],['doubt','Doubt Clearing'],['support','Support'],['adhoc','Ad-hoc'],['recorded','Recorded']].map(([type, label]) => (
                      <div key={type} className="flex items-center gap-2">
                        <div className={cn('w-2.5 h-2.5 rounded-full flex-shrink-0', calColor(type).bg)} />
                        <span className="text-[11px] text-muted-foreground">{label}</span>
                      </div>
                    ))}
                    <div className="border-t border-border/50 pt-1.5 mt-1.5 space-y-1.5">
                      {[['bg-success animate-pulse','Live Now'],['bg-primary','Upcoming'],['bg-muted-foreground','Completed'],['bg-destructive','Cancelled']].map(([cls, label]) => (
                        <div key={label} className="flex items-center gap-2">
                          <div className={cn('w-2.5 h-2.5 rounded-full flex-shrink-0', cls)} />
                          <span className="text-[11px] text-muted-foreground">{label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ── DynamicProgramsTab ─────────────────────────────────────────────────────────
const DynamicProgramsTab = ({ mentors, listLoading }) => {
  const [search,        setSearch]        = useState('');
  const [domainFilter,  setDomainFilter]  = useState('all');
  const [mentorFilter,  setMentorFilter]  = useState(new Set()); // empty = show all
  const [dropOpen,      setDropOpen]      = useState(false);
  const [expandedIds,   setExpandedIds]   = useState(new Set());
  const dropRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Aggregate programs across all mentors; de-dupe by programId, track per-mentor assignments
  const programMap = useMemo(() => {
    const map = {};
    for (const m of mentors) {
      for (const prog of (m.assignedPrograms || [])) {
        const pid = String(prog.programId);
        if (!map[pid]) map[pid] = { ...prog, _assignments: [] };
        map[pid]._assignments.push({
          mentorId:   String(m._id),
          mentorName: m.name,
          status:     m.status,
          assignedAt: prog.assignedAt,
        });
      }
    }
    return map;
  }, [mentors]);

  const allPrograms = useMemo(() => Object.values(programMap), [programMap]);

  // Unique domains for filter pills
  const domains = useMemo(() =>
    Array.from(new Set(allPrograms.map(p => p.domain).filter(Boolean))).sort(),
    [allPrograms]
  );

  // Active mentors for mentor-filter dropdown
  const activeMentors = mentors.filter(m =>
    ['Active', 'Integration Setup Required'].includes(m.status)
  );

  const toggleMentorFilter = (id) => {
    setMentorFilter(prev => {
      const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
    });
  };

  const toggleExpand = (pid) => {
    setExpandedIds(prev => {
      const next = new Set(prev); next.has(pid) ? next.delete(pid) : next.add(pid); return next;
    });
  };

  // Apply filters
  const filtered = useMemo(() => {
    let list = allPrograms;
    if (domainFilter !== 'all') list = list.filter(p => p.domain === domainFilter);
    if (mentorFilter.size > 0)  list = list.filter(p => p._assignments.some(a => mentorFilter.has(a.mentorId)));
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        p.title?.toLowerCase().includes(q) ||
        p.domain?.toLowerCase().includes(q) ||
        p.jobRole?.toLowerCase().includes(q) ||
        p.focusArea?.toLowerCase().includes(q) ||
        (p.industries  || []).some(x => x.toLowerCase().includes(q)) ||
        (p.aiTools     || []).some(x => x.toLowerCase().includes(q)) ||
        (p.careerPaths || []).some(x => x.toLowerCase().includes(q))
      );
    }
    return [...list].sort((a, b) =>
      b._assignments.length - a._assignments.length || (a.title || '').localeCompare(b.title || '')
    );
  }, [allPrograms, domainFilter, mentorFilter, search]);


  if (listLoading) {
    return (
      <div className="flex items-center justify-center py-16 gap-2 text-muted-foreground">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading programs…
      </div>
    );
  }

  return (
    <div className="space-y-5">

      {/* ── Toolbar ── */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Search */}
        <div className="relative">
          <BookOpen className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <input
            placeholder="Search programs, domain, role, tools…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8 pr-3 py-1.5 rounded-lg border border-border bg-background text-sm w-64 focus:outline-none focus:ring-1 focus:ring-primary/40"
          />
        </div>

        {/* Mentor filter dropdown */}
        <div className="relative" ref={dropRef}>
          <button onClick={() => setDropOpen(v => !v)}
            className={cn(
              'flex items-center gap-2 px-3.5 py-2 rounded-xl border text-sm font-medium transition-colors',
              mentorFilter.size > 0
                ? 'border-primary/40 bg-primary/8 text-primary'
                : 'border-border bg-card text-foreground hover:bg-muted/40'
            )}>
            <GraduationCap className="w-4 h-4" />
            {mentorFilter.size === 0 ? 'All Mentors' : `${mentorFilter.size} Mentor${mentorFilter.size > 1 ? 's' : ''}`}
            <ChevronRight className={cn('w-3.5 h-3.5 transition-transform', dropOpen && 'rotate-90')} />
          </button>

          {dropOpen && (
            <div className="absolute z-50 top-full left-0 mt-1.5 bg-card border border-border rounded-xl shadow-xl w-64 overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/30">
                <span className="text-[11px] font-semibold text-muted-foreground">Filter by Mentor</span>
                {mentorFilter.size > 0 && (
                  <button onClick={() => setMentorFilter(new Set())}
                    className="text-[11px] font-medium text-primary hover:underline">Clear</button>
                )}
              </div>
              <div className="max-h-56 overflow-y-auto divide-y divide-border/40">
                {activeMentors.map(m => {
                  const id    = String(m._id);
                  const isSel = mentorFilter.has(id);
                  const count = programMap ? Object.values(programMap).filter(p => p._assignments.some(a => a.mentorId === id)).length : 0;
                  return (
                    <label key={id}
                      className={cn('flex items-center gap-2.5 px-3 py-2.5 cursor-pointer transition-colors select-none',
                        isSel ? 'bg-primary/5' : 'hover:bg-muted/40'
                      )}>
                      <input type="checkbox" checked={isSel} onChange={() => toggleMentorFilter(id)} className="rounded accent-primary" />
                      <div className={cn('w-7 h-7 rounded-full gradient-primary flex items-center justify-center text-[10px] font-bold text-primary-foreground flex-shrink-0',
                        isSel && 'ring-2 ring-primary/40')}>
                        {m.name.split(' ').slice(0,2).map(n=>n[0]).join('')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-foreground truncate">{m.name}</p>
                        <p className="text-[10px] text-muted-foreground">{count} program{count !== 1 ? 's' : ''} assigned</p>
                      </div>
                    </label>
                  );
                })}
                {activeMentors.length === 0 && (
                  <div className="py-5 text-center text-xs text-muted-foreground">No active mentors</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Domain filter pills */}
        <div className="flex gap-1.5 flex-wrap">
          {['all', ...domains].map(d => (
            <button key={d} onClick={() => setDomainFilter(d)}
              className={cn('text-xs px-3 py-1.5 rounded-full font-medium border capitalize transition-colors',
                domainFilter === d
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border text-muted-foreground hover:text-foreground bg-transparent'
              )}>{d === 'all' ? `All (${allPrograms.length})` : d.replace(/_/g, ' ')}</button>
          ))}
        </div>

        <span className="ml-auto text-xs text-muted-foreground">{filtered.length} program{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Active mentor filter chips */}
      {mentorFilter.size > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {Array.from(mentorFilter).map(id => {
            const m = mentors.find(x => String(x._id) === id);
            return (
              <span key={id} className="flex items-center gap-1.5 text-xs bg-primary/8 border border-primary/20 text-primary rounded-full px-2.5 py-1 font-medium">
                {m?.name || 'Unknown'}
                <button onClick={() => toggleMentorFilter(id)} className="hover:text-destructive transition-colors">
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {/* ── Empty state ── */}
      {allPrograms.length === 0 && (
        <div className="text-center py-20 border-2 border-dashed border-border rounded-2xl">
          <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-7 h-7 text-muted-foreground opacity-40" />
          </div>
          <p className="text-base font-semibold text-foreground mb-1">No Programs Assigned Yet</p>
          <p className="text-sm text-muted-foreground max-w-sm mx-auto">
            Programs appear here once the admin assigns them to your mentors on MentorNexus.
          </p>
        </div>
      )}

      {filtered.length === 0 && allPrograms.length > 0 && (
        <div className="text-center py-12 border-2 border-dashed border-border rounded-2xl">
          <BookOpen className="w-8 h-8 text-muted-foreground mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium text-foreground mb-1">No programs match this filter</p>
          <p className="text-xs text-muted-foreground">Try clearing the domain filter or mentor selection.</p>
        </div>
      )}

      {/* ── Flat program grid ── */}
      {filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map(prog => {
            const pid        = String(prog.programId);
            const isExpanded = expandedIds.has(pid);
            const hasExtra   = prog.jobRole || prog.focusArea || (prog.aiTools?.length > 0) ||
              (prog.industries?.length > 0) || (prog.careerPaths?.length > 0) || prog.notes;

              return (
                <div key={pid} className="rounded-xl border border-border bg-card overflow-hidden hover:shadow-sm transition-shadow">

                  {/* Card header */}
                  <div className="p-4">
                    <div className="flex gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <BookOpen className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground leading-snug">{prog.title || 'Untitled Program'}</p>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                          {prog.domain && (
                            <span className={cn('text-[11px] px-2 py-0.5 rounded-full border font-medium', domainBadge(prog.domain))}>
                              {prog.domain.replace(/_/g, ' ')}
                            </span>
                          )}
                          {prog.level && (
                            <span className="text-[11px] px-2 py-0.5 rounded-full border bg-muted text-muted-foreground font-medium">
                              {prog.level}
                            </span>
                          )}
                          {prog.totalWeeks && (
                            <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                              <Clock className="w-3 h-3" />{prog.totalWeeks}w
                            </span>
                          )}
                          {prog.programType && (
                            <span className="text-[11px] text-muted-foreground capitalize">{prog.programType}</span>
                          )}
                        </div>
                        {prog.levelDesc && (
                          <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{prog.levelDesc}</p>
                        )}
                      </div>
                    </div>

                    {/* Assigned mentors */}
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] text-muted-foreground font-medium">Assigned to:</span>
                      <div className="flex items-center gap-1 flex-wrap">
                        {prog._assignments.map((a, i) => (
                          <div key={a.mentorId} className="flex items-center gap-1 bg-muted/50 border border-border/60 rounded-full pl-0.5 pr-2 py-0.5">
                            <div className="w-5 h-5 rounded-full gradient-primary flex items-center justify-center text-[9px] font-bold text-primary-foreground flex-shrink-0">
                              {a.mentorName.split(' ').slice(0,2).map(n=>n[0]).join('')}
                            </div>
                            <span className="text-[11px] font-medium text-foreground">{a.mentorName.split(' ')[0]}</span>
                            {a.assignedAt && (
                              <span className="text-[10px] text-muted-foreground hidden sm:inline">
                                · {new Date(a.assignedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Quick meta row */}
                    <div className="flex flex-wrap gap-3 mt-3 text-[11px] text-muted-foreground">
                      {prog.programRef && (
                        <span className="font-mono bg-muted px-1.5 py-0.5 rounded text-[10px]">{prog.programRef}</span>
                      )}
                      {prog.collection && (
                        <span className="capitalize">{prog.collection.replace(/_/g, ' ')}</span>
                      )}
                      <span className="text-foreground font-medium">{prog._assignments.length} mentor{prog._assignments.length !== 1 ? 's' : ''} assigned</span>
                    </div>
                  </div>

                  {/* Expandable details */}
                  {hasExtra && (
                    <>
                      <div className="border-t border-border/50 px-4 py-2 bg-muted/10">
                        <button onClick={() => toggleExpand(pid)}
                          className="text-xs text-primary font-medium flex items-center gap-1 hover:underline">
                          {isExpanded ? 'Hide details' : 'View full details'}
                          <ChevronRight className={cn('w-3.5 h-3.5 transition-transform', isExpanded && 'rotate-90')} />
                        </button>
                      </div>

                      {isExpanded && (
                        <div className="px-4 pb-4 pt-3 space-y-3 border-t border-border/50 bg-muted/5">

                          {prog.jobRole && (
                            <div>
                              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Job Role</p>
                              <p className="text-sm text-foreground">{prog.jobRole}</p>
                            </div>
                          )}

                          {prog.focusArea && (
                            <div>
                              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Focus Area</p>
                              <p className="text-sm text-foreground">{prog.focusArea}</p>
                            </div>
                          )}

                          {prog.aiTools?.length > 0 && (
                            <div>
                              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">AI Tools</p>
                              <div className="flex flex-wrap gap-1.5">
                                {prog.aiTools.map((t, i) => (
                                  <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-primary/8 border border-primary/20 text-primary font-medium">{t}</span>
                                ))}
                              </div>
                            </div>
                          )}

                          {prog.industries?.length > 0 && (
                            <div>
                              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Target Industries</p>
                              <div className="flex flex-wrap gap-1.5">
                                {prog.industries.map((t, i) => (
                                  <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-muted border border-border text-muted-foreground">{t}</span>
                                ))}
                              </div>
                            </div>
                          )}

                          {prog.careerPaths?.length > 0 && (
                            <div>
                              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Career Paths</p>
                              <div className="flex flex-wrap gap-1.5">
                                {prog.careerPaths.map((t, i) => (
                                  <span key={i} className="text-[11px] px-2 py-0.5 rounded bg-success/8 border border-success/20 text-success">{t}</span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Per-mentor assignment dates */}
                          {prog._assignments.length > 1 && (
                            <div>
                              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Assignment Dates</p>
                              <div className="space-y-1">
                                {prog._assignments.map(a => (
                                  <div key={a.mentorId} className="flex items-center justify-between text-[11px]">
                                    <span className="text-foreground font-medium">{a.mentorName}</span>
                                    <span className="text-muted-foreground">
                                      {a.assignedAt
                                        ? new Date(a.assignedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                                        : '—'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {prog.notes && (
                            <div>
                              <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Admin Notes</p>
                              <p className="text-xs text-foreground italic bg-muted/40 px-3 py-2 rounded-lg">{prog.notes}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── DynamicBatchesTab ──────────────────────────────────────────────────────────
const DynamicBatchesTab = ({ mentors, listLoading }) => {
  const [selectedIds,  setSelectedIds]  = useState(new Set());
  const [analyticsMap, setAnalyticsMap] = useState({});
  const [loadingSet,   setLoadingSet]   = useState(new Set());
  const [errorMap,     setErrorMap]     = useState({});
  const [dropOpen,     setDropOpen]     = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [batchSearch,  setBatchSearch]  = useState('');
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [batchSubTab,   setBatchSubTab]   = useState({});
  const autoInitDone = useRef(false);
  const dropRef = useRef(null);

  const activeMentors = mentors.filter(m =>
    ['Active', 'Integration Setup Required'].includes(m.status)
  );

  // Auto-select all active mentors on first load (mirrors Programs tab behaviour)
  useEffect(() => {
    if (autoInitDone.current || listLoading || activeMentors.length === 0) return;
    autoInitDone.current = true;
    const ids = activeMentors.map(m => String(m._id));
    setSelectedIds(new Set(ids));
    ids.forEach(id => {
      setLoadingSet(prev => new Set([...prev, id]));
      apiGetMentorAnalytics(id)
        .then(data => setAnalyticsMap(prev => ({ ...prev, [id]: data })))
        .catch(e => setErrorMap(prev => ({ ...prev, [id]: e.message })))
        .finally(() => setLoadingSet(prev => { const s = new Set(prev); s.delete(id); return s; }));
    });
  }, [listLoading, activeMentors.length]);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchForMentor = (id) => {
    if (analyticsMap[id] || loadingSet.has(id)) return;
    setLoadingSet(prev => new Set([...prev, id]));
    apiGetMentorAnalytics(id)
      .then(data => setAnalyticsMap(prev => ({ ...prev, [id]: data })))
      .catch(e => setErrorMap(prev => ({ ...prev, [id]: e.message })))
      .finally(() => setLoadingSet(prev => { const s = new Set(prev); s.delete(id); return s; }));
  };

  const toggleMentor = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); fetchForMentor(id); }
      return next;
    });
  };

  const selectAllActive = () => {
    const ids = activeMentors.map(m => String(m._id));
    setSelectedIds(new Set(ids));
    ids.forEach(id => fetchForMentor(id));
    setDropOpen(false);
  };

  const clearAll = () => { setSelectedIds(new Set()); setDropOpen(false); };

  const subTabFor  = (bid) => batchSubTab[bid] || 'sessions';
  const setSubTab  = (bid, v) => setBatchSubTab(prev => ({ ...prev, [bid]: v }));

  // Build flat tagged batch list from all selected mentors
  const allTaggedBatches = Array.from(selectedIds).flatMap(id => {
    const m = mentors.find(x => String(x._id) === id);
    return (analyticsMap[id]?.batches || []).map(b => ({
      ...b,
      _mentorId:   id,
      _mentorName: m?.name || 'Unknown',
      _uid:        `${String(b._id)}_${id}`,
    }));
  });

  const STATUS_MATCH = {
    active:    ['active', 'upcoming'],
    upcoming:  ['upcoming'],
    completed: ['completed'],
    draft:     ['draft', 'pending_review'],
  };
  const filteredBatches = allTaggedBatches.filter(b => {
    if (statusFilter !== 'all' && !(STATUS_MATCH[statusFilter] || [statusFilter]).includes(b.status)) return false;
    if (batchSearch) {
      const q = batchSearch.toLowerCase();
      return b.batchName?.toLowerCase().includes(q) ||
        (b.programLabels || []).some(l => l.toLowerCase().includes(q)) ||
        b._mentorName.toLowerCase().includes(q);
    }
    return true;
  });

  const agg = {
    totalBatches:      allTaggedBatches.length,
    activeBatches:     allTaggedBatches.filter(b => ['active','upcoming'].includes(b.status)).length,
    completedBatches:  allTaggedBatches.filter(b => b.status === 'completed').length,
    totalSessions:     allTaggedBatches.reduce((t, b) => t + (b.stats?.totalSessions || 0), 0),
    completedSessions: allTaggedBatches.reduce((t, b) => t + (b.stats?.completedSessions || 0), 0),
    totalEnrollments:  allTaggedBatches.reduce((t, b) => t + (b.stats?.activeEnrollments || 0), 0),
    totalTaRequests:   allTaggedBatches.reduce((t, b) => t + (b.stats?.totalTaRequests || 0), 0),
    pendingTaRequests: allTaggedBatches.reduce((t, b) => t + (b.stats?.pendingTaRequests || 0), 0),
  };

  const anyLoading = loadingSet.size > 0;

  return (
    <div className="space-y-5">

      {/* ── Mentor selector row ── */}
      <div className="flex items-start gap-3 flex-wrap">
        <div className="relative" ref={dropRef}>
          <button onClick={() => setDropOpen(v => !v)}
            className={cn(
              'flex items-center gap-2 px-3.5 py-2 rounded-xl border text-sm font-medium transition-colors',
              selectedIds.size > 0
                ? 'border-primary/40 bg-primary/8 text-primary'
                : 'border-border bg-card text-foreground hover:bg-muted/40'
            )}>
            <GraduationCap className="w-4 h-4" />
            {selectedIds.size === 0
              ? 'Select Mentors'
              : `${selectedIds.size} Mentor${selectedIds.size > 1 ? 's' : ''} Selected`}
            {anyLoading && <RefreshCw className="w-3.5 h-3.5 animate-spin ml-0.5" />}
            <ChevronRight className={cn('w-3.5 h-3.5 transition-transform', dropOpen && 'rotate-90')} />
          </button>

          {dropOpen && (
            <div className="absolute z-50 top-full left-0 mt-1.5 bg-card border border-border rounded-xl shadow-xl w-72 overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-muted/30">
                <button onClick={selectAllActive}
                  className="text-[11px] font-semibold text-primary hover:underline">
                  Select All Active ({activeMentors.length})
                </button>
                <button onClick={clearAll}
                  className="text-[11px] font-medium text-muted-foreground hover:text-foreground">
                  Clear All
                </button>
              </div>

              {listLoading ? (
                <div className="flex items-center justify-center py-6 gap-2 text-muted-foreground text-xs">
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Loading mentors…
                </div>
              ) : activeMentors.length === 0 ? (
                <div className="py-6 text-center text-xs text-muted-foreground">No active mentors found</div>
              ) : (
                <div className="max-h-64 overflow-y-auto divide-y divide-border/40">
                  {activeMentors.map(m => {
                    const id       = String(m._id);
                    const isSel    = selectedIds.has(id);
                    const isLoad   = loadingSet.has(id);
                    const hasErr   = !!errorMap[id];
                    const bc       = analyticsMap[id]?.summary?.totalBatches;
                    return (
                      <label key={id}
                        className={cn('flex items-center gap-2.5 px-3 py-2.5 cursor-pointer transition-colors select-none',
                          isSel ? 'bg-primary/5' : 'hover:bg-muted/40'
                        )}>
                        <input type="checkbox" checked={isSel} onChange={() => toggleMentor(id)}
                          className="rounded accent-primary" />
                        <div className={cn(
                          'w-8 h-8 rounded-full gradient-primary flex items-center justify-center text-[11px] font-bold text-primary-foreground flex-shrink-0',
                          isSel && 'ring-2 ring-primary/50'
                        )}>
                          {m.name.split(' ').slice(0,2).map(n=>n[0]).join('')}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-foreground truncate">{m.name}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {bc != null
                              ? `${bc} batch${bc !== 1 ? 'es' : ''} · ${m.status}`
                              : m.status}
                          </p>
                        </div>
                        {isLoad && <RefreshCw className="w-3 h-3 animate-spin text-muted-foreground flex-shrink-0" />}
                        {hasErr  && <AlertCircle className="w-3 h-3 text-destructive flex-shrink-0" title={errorMap[id]} />}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Selected mentor chips */}
        <div className="flex flex-wrap gap-1.5 items-center">
          {Array.from(selectedIds).map(id => {
            const m = mentors.find(x => String(x._id) === id);
            return (
              <span key={id} className="flex items-center gap-1.5 text-xs bg-primary/8 border border-primary/20 text-primary rounded-full px-2.5 py-1 font-medium">
                <div className="w-4 h-4 rounded-full gradient-primary flex items-center justify-center text-[8px] font-bold text-primary-foreground">
                  {(m?.name || '?').split(' ').slice(0,2).map(n=>n[0]).join('')}
                </div>
                {m?.name || 'Unknown'}
                <button onClick={() => toggleMentor(id)} className="hover:text-destructive transition-colors ml-0.5">
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      </div>

      {/* ── Empty state — no mentor selected ── */}
      {selectedIds.size === 0 && !listLoading && activeMentors.length === 0 && (
        <div className="text-center py-20 border-2 border-dashed border-border rounded-2xl">
          <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
            <GraduationCap className="w-8 h-8 text-muted-foreground opacity-50" />
          </div>
          <p className="text-base font-semibold text-foreground mb-1">No Active Mentors Yet</p>
          <p className="text-sm text-muted-foreground max-w-sm mx-auto">
            Batches will appear here once mentors are onboarded and their status becomes Active.
          </p>
        </div>
      )}

      {selectedIds.size === 0 && listLoading && (
        <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground text-sm">
          <RefreshCw className="w-4 h-4 animate-spin" /> Loading mentor list…
        </div>
      )}

      {/* ── Content ── */}
      {selectedIds.size > 0 && (
        <div className="space-y-5">
          {/* Aggregate KPI strip */}
          {allTaggedBatches.length > 0 && (
            <div className="grid grid-cols-4 md:grid-cols-7 gap-2">
              {[
                { label: 'Total Batches',   value: agg.totalBatches,      icon: Layers,        color: 'text-primary',   bg: 'bg-primary/8'   },
                { label: 'Active/Upcoming', value: agg.activeBatches,     icon: CheckCircle,   color: 'text-success',   bg: 'bg-success/8'   },
                { label: 'Completed',       value: agg.completedBatches,  icon: Award,         color: 'text-chart-3',   bg: 'bg-chart-3/8'   },
                { label: 'Total Sessions',  value: agg.totalSessions,     icon: Calendar,      color: 'text-chart-2',   bg: 'bg-chart-2/8'   },
                { label: 'Done Sessions',   value: agg.completedSessions, icon: CheckCircle,   color: 'text-chart-2',   bg: 'bg-chart-2/8'   },
                { label: 'Enrollments',     value: agg.totalEnrollments,  icon: Users,         color: 'text-chart-4',   bg: 'bg-chart-4/8'   },
                { label: 'Support Req.',    value: agg.totalTaRequests,   icon: MessageSquare, color: 'text-warning',   bg: 'bg-warning/8'   },
              ].map(k => (
                <div key={k.label} className={cn('p-2.5 rounded-xl border border-border text-center relative', k.bg)}>
                  <k.icon className={cn('w-3.5 h-3.5 mx-auto mb-1', k.color)} />
                  <div className="font-bold text-foreground text-sm">{k.value}</div>
                  <div className="text-[10px] text-muted-foreground leading-tight">{k.label}</div>
                  {k.label === 'Support Req.' && agg.pendingTaRequests > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-warning text-[9px] font-bold text-warning-foreground rounded-full flex items-center justify-center">
                      {agg.pendingTaRequests}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative">
              <BookOpen className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                placeholder="Search by batch, program, mentor…"
                value={batchSearch}
                onChange={e => setBatchSearch(e.target.value)}
                className="pl-8 pr-3 py-1.5 rounded-lg border border-border bg-background text-sm w-60 focus:outline-none focus:ring-1 focus:ring-primary/40"
              />
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {[
                ['all', `All (${allTaggedBatches.length})`],
                ['active', 'Active'],
                ['upcoming', 'Upcoming'],
                ['completed', 'Completed'],
                ['draft', 'Draft'],
              ].map(([v, l]) => (
                <button key={v} onClick={() => setStatusFilter(v)}
                  className={cn('text-xs px-3 py-1.5 rounded-full font-medium border transition-colors capitalize',
                    statusFilter === v
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border text-muted-foreground hover:text-foreground bg-transparent'
                  )}>{l}</button>
              ))}
            </div>
            <span className="ml-auto text-xs text-muted-foreground">{filteredBatches.length} batch{filteredBatches.length !== 1 ? 'es' : ''}</span>
          </div>

          {/* Loading progress */}
          {anyLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/30 border border-border/60 rounded-xl px-4 py-2.5">
              <RefreshCw className="w-4 h-4 animate-spin text-primary" />
              Fetching batch data for {loadingSet.size} mentor{loadingSet.size > 1 ? 's' : ''}…
            </div>
          )}

          {/* Error chips */}
          {Object.keys(errorMap).filter(id => selectedIds.has(id)).map(id => {
            const m = mentors.find(x => String(x._id) === id);
            return (
              <div key={id} className="flex items-center gap-2 text-xs text-destructive bg-destructive/8 border border-destructive/20 rounded-xl px-3 py-2">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                Failed to load batches for <strong>{m?.name}</strong>: {errorMap[id]}
                <button onClick={() => {
                  setErrorMap(prev => { const n = {...prev}; delete n[id]; return n; });
                  fetchForMentor(id);
                }} className="ml-auto underline font-medium">Retry</button>
              </div>
            );
          })}

          {/* Empty result */}
          {filteredBatches.length === 0 && !anyLoading && (
            <div className="text-center py-14 border-2 border-dashed border-border rounded-2xl">
              <Layers className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium text-foreground mb-1">No batches found</p>
              <p className="text-xs text-muted-foreground">
                {allTaggedBatches.length > 0
                  ? 'Try adjusting the status filter or search term.'
                  : 'Selected mentors have no batches yet. Batches appear once created on MentorNexus.'}
              </p>
            </div>
          )}

          {/* ── Batch cards ── */}
          <div className="space-y-4">
            {filteredBatches.map(batch => {
              const bm      = batchStatusMeta(batch.status);
              const uid     = batch._uid;
              const isOpen  = expandedBatch === uid;
              const subTab  = subTabFor(uid);
              const occ     = batch.stats?.occupancyPct || 0;
              const occColor = occ >= 90 ? 'bg-destructive' : occ >= 70 ? 'bg-warning' : 'bg-success';

              return (
                <div key={uid} className="rounded-xl border border-border bg-card overflow-hidden">

                  {/* Mentor attribution chip */}
                  <div className="flex items-center gap-2 px-4 pt-3 pb-0">
                    <div className="w-5 h-5 rounded-full gradient-primary flex items-center justify-center text-[9px] font-bold text-primary-foreground flex-shrink-0">
                      {batch._mentorName.split(' ').slice(0,2).map(n=>n[0]).join('')}
                    </div>
                    <span className="text-[11px] font-semibold text-primary">{batch._mentorName}</span>
                    <span className="text-[10px] text-muted-foreground">· Mentor</span>
                  </div>

                  {/* Batch header */}
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-foreground">{batch.batchName}</p>
                          {batch.batchCode && (
                            <span className="text-[10px] text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded">
                              {batch.batchCode}
                            </span>
                          )}
                        </div>
                        {batch.customLabel && (
                          <p className="text-xs text-muted-foreground mt-0.5">"{batch.customLabel}"</p>
                        )}
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          <span className={cn('text-[11px] px-2 py-0.5 rounded-full border font-medium', bm.cls)}>{bm.label}</span>
                          <span className="text-[11px] px-2 py-0.5 rounded-full border bg-muted text-muted-foreground capitalize">{batch.sessionMode}</span>
                          <span className="text-[11px] px-2 py-0.5 rounded-full border bg-muted text-muted-foreground capitalize">
                            {batch.batchType?.replace('_', ' ')}
                          </span>
                          {(batch.programLabels || []).slice(0, 2).map((pl, i) => (
                            <span key={i} className="text-[11px] px-2 py-0.5 rounded-full border bg-primary/8 text-primary border-primary/20">{pl}</span>
                          ))}
                        </div>
                      </div>
                      <button
                        onClick={() => setExpandedBatch(isOpen ? null : uid)}
                        className="flex-shrink-0 p-1.5 rounded-lg hover:bg-muted transition-colors">
                        <ChevronRight className={cn('w-4 h-4 text-muted-foreground transition-transform', isOpen && 'rotate-90')} />
                      </button>
                    </div>

                    {/* Occupancy bar */}
                    <div className="mt-3">
                      <div className="flex justify-between text-[11px] mb-1">
                        <span className="text-muted-foreground">Enrollment</span>
                        <span className="font-semibold text-foreground">
                          {batch.enrolledCount} / {batch.maxStudents} students ({occ}%)
                        </span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden">
                        <div className={cn('h-full rounded-full transition-all', occColor)} style={{ width: `${Math.min(occ, 100)}%` }} />
                      </div>
                    </div>

                    {/* Schedule grid */}
                    <div className="grid grid-cols-3 gap-2 mt-3">
                      {[
                        ['Start',        batch.scheduleConfig?.startDate],
                        ['End',          batch.scheduleConfig?.endDate],
                        ['Enrol By',     batch.scheduleConfig?.enrolmentDeadline],
                      ].map(([lbl, d]) => (
                        <div key={lbl} className="text-center p-2 rounded-lg bg-muted/50">
                          <p className="text-[10px] text-muted-foreground">{lbl}</p>
                          <p className="text-xs font-semibold text-foreground">{fmtDate(d)}</p>
                        </div>
                      ))}
                    </div>

                    {/* Stats strip */}
                    <div className="flex gap-3 mt-3 flex-wrap text-[11px]">
                      <span className="text-muted-foreground">
                        <span className="font-semibold text-foreground">{batch.stats?.completedSessions}</span>
                        /{batch.stats?.totalSessions} sessions done
                      </span>
                      {batch.stats?.avgAttendancePct != null && (
                        <span className="text-muted-foreground">
                          Avg attendance <span className="font-semibold text-foreground">{batch.stats.avgAttendancePct}%</span>
                        </span>
                      )}
                      {batch.scheduleConfig?.totalWeeks && (
                        <span className="text-muted-foreground">
                          <span className="font-semibold text-foreground">{batch.scheduleConfig.totalWeeks}</span>w
                          {batch.scheduleConfig.hoursPerWeek ? ` · ${batch.scheduleConfig.hoursPerWeek}h/w` : ''}
                        </span>
                      )}
                      {(batch.stats?.pendingTaRequests || 0) > 0 && (
                        <span className="font-semibold text-warning">
                          {batch.stats.pendingTaRequests} support pending
                        </span>
                      )}
                      {(batch.stats?.pendingTaRequests || 0) === 0 && (batch.stats?.assignedTaRequests || 0) > 0 && (
                        <span className="text-muted-foreground">
                          {batch.stats.assignedTaRequests} support assigned
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Expandable detail */}
                  {isOpen && (
                    <div className="border-t border-border">
                      {/* Sub-tabs */}
                      <div className="flex border-b border-border bg-muted/20">
                        {[
                          { id: 'sessions',    label: `Sessions (${batch.stats?.totalSessions || 0})` },
                          { id: 'support',     label: `Support (${batch.taRequests?.length || 0})` },
                          { id: 'enrollments', label: 'Enrollments' },
                        ].map(st => (
                          <button key={st.id} onClick={() => setSubTab(uid, st.id)}
                            className={cn('px-3 py-2.5 text-[11px] font-semibold border-b-2 transition-colors',
                              subTab === st.id
                                ? 'border-primary text-primary bg-background'
                                : 'border-transparent text-muted-foreground hover:text-foreground'
                            )}>{st.label}</button>
                        ))}
                      </div>

                      <div className="p-4 space-y-3">

                        {/* Sessions */}
                        {subTab === 'sessions' && (
                          <>
                            {(batch.upcomingSessions || []).length > 0 && (
                              <div>
                                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">Upcoming</p>
                                <div className="space-y-2">
                                  {(batch.upcomingSessions || []).map((s, i) => (
                                    <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/40 border border-border">
                                      <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-0.5" />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-semibold text-foreground truncate">{s.title}</p>
                                        <p className="text-[11px] text-muted-foreground">
                                          {fmtTimeRange(s.scheduledAt, s.durationMinutes)}
                                          {' · '}{new Date(s.scheduledAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                          {s.weekNumber ? ` · W${s.weekNumber}` : ''}
                                          {s.dayNumber  ? `/D${s.dayNumber}`    : ''}
                                          {s.assignedTaName ? <span className="text-chart-3"> · TA: {s.assignedTaName}</span> : ''}
                                        </p>
                                      </div>
                                      <div className="flex items-center gap-1.5 flex-shrink-0">
                                        <span className="text-[10px] px-1.5 py-0.5 rounded border bg-muted border-border text-muted-foreground capitalize">
                                          {s.sessionTypeLabel}
                                        </span>
                                        {s.teamsLink && (
                                          <a href={s.teamsLink} target="_blank" rel="noreferrer"
                                            className="text-[10px] text-info underline">Join</a>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {(batch.recentSessions || []).length > 0 && (
                              <div>
                                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">Recently Completed</p>
                                <div className="space-y-2">
                                  {(batch.recentSessions || []).map((s, i) => (
                                    <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/40 border border-border">
                                      <CheckCircle className="w-3.5 h-3.5 text-success flex-shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-semibold text-foreground truncate">{s.title}</p>
                                        <p className="text-[11px] text-muted-foreground">
                                          {fmtTimeRange(s.scheduledAt, s.durationMinutes)}
                                          {' · '}{new Date(s.scheduledAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                        </p>
                                      </div>
                                      <div className="flex items-center gap-2 text-[11px] text-muted-foreground flex-shrink-0">
                                        <span>
                                          <span className="font-semibold text-foreground">{s.attendanceCount}</span>/{s.totalAttendees} present
                                        </span>
                                        {s.recordingUrl && (
                                          <a href={s.recordingUrl} target="_blank" rel="noreferrer"
                                            className="text-info underline text-[10px]">Rec</a>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                  {(batch.stats?.completedSessions || 0) > 5 && (
                                    <p className="text-[11px] text-muted-foreground text-center">
                                      +{(batch.stats.completedSessions) - 5} more completed sessions
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}

                            {(batch.upcomingSessions || []).length === 0 && (batch.recentSessions || []).length === 0 && (
                              <p className="text-xs text-muted-foreground text-center py-4">No sessions scheduled yet.</p>
                            )}
                          </>
                        )}

                        {/* Support Staff */}
                        {subTab === 'support' && (
                          <>
                            {(batch.taRequests || []).length === 0 ? (
                              <p className="text-xs text-muted-foreground text-center py-4">No support staff requested for this batch.</p>
                            ) : (batch.taRequests || []).map((ta, i) => {
                              const tm = TA_STATUS_META[ta.status] || TA_STATUS_META.pending;
                              return (
                                <div key={i} className="p-3 rounded-xl border border-border bg-muted/30 space-y-2">
                                  <div className="flex items-center justify-between gap-2">
                                    <div>
                                      <p className="text-xs font-semibold text-foreground">{TA_TYPE_LABEL[ta.requestType] || ta.requestType}</p>
                                      <p className="text-[11px] text-muted-foreground capitalize">
                                        {ta.sessionSubtype?.replace(/_/g, ' ') || 'General support'}
                                      </p>
                                    </div>
                                    <span className={cn('text-[11px] px-2 py-0.5 rounded-full border font-medium', tm.cls)}>{tm.label}</span>
                                  </div>
                                  <p className="text-xs text-foreground"><span className="text-muted-foreground">Topic: </span>{ta.topic}</p>
                                  {ta.scheduledAt && (
                                    <p className="text-[11px] text-muted-foreground">
                                      {fmtTimeRange(ta.scheduledAt, ta.durationMinutes)}
                                      {' · '}{new Date(ta.scheduledAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                      {ta.staffCount > 1 ? ` · ${ta.staffCount} staff needed` : ''}
                                    </p>
                                  )}
                                  {(ta.assignedTaNames?.length > 0 || ta.assignedTaName) && (
                                    <p className="text-[11px] text-success font-medium">
                                      Assigned: {ta.assignedTaNames?.join(', ') || ta.assignedTaName}
                                    </p>
                                  )}
                                  {ta.preferredSkills?.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                      {ta.preferredSkills.map((sk, j) => (
                                        <span key={j} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{sk}</span>
                                      ))}
                                    </div>
                                  )}
                                  {ta.notes && (
                                    <p className="text-[11px] text-muted-foreground italic">{ta.notes}</p>
                                  )}
                                </div>
                              );
                            })}
                          </>
                        )}

                        {/* Enrollments */}
                        {subTab === 'enrollments' && (
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-2">
                              {[
                                ['Active',          batch.enrollmentSummary?.active || 0,          'text-success'],
                                ['Consent Pending', batch.enrollmentSummary?.consentPending || 0,  'text-warning'],
                                ['Shifted Out',     batch.enrollmentSummary?.shifted || 0,         'text-info'],
                                ['Withdrawn',       batch.enrollmentSummary?.withdrawn || 0,       'text-destructive'],
                              ].map(([lbl, cnt, cls]) => (
                                <div key={lbl} className="p-2.5 rounded-lg bg-muted/50 border border-border flex items-center justify-between">
                                  <span className="text-[11px] text-muted-foreground">{lbl}</span>
                                  <span className={cn('text-sm font-bold', cls)}>{cnt}</span>
                                </div>
                              ))}
                            </div>

                            {batch.feeConfig?.batchType === 'free' ? (
                              <div className="p-3 rounded-xl bg-success/8 border border-success/20 text-success text-xs font-medium">
                                Free Batch — No Student Fee
                              </div>
                            ) : batch.feeConfig?.totalStudentPrice > 0 && (
                              <div className="p-3 rounded-xl bg-muted/40 border border-border">
                                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-2">Fee Structure</p>
                                <div className="flex gap-4 text-xs">
                                  <span className="text-muted-foreground">
                                    Student Fee: <span className="font-semibold text-foreground">
                                      ₹{Number(batch.feeConfig.totalStudentPrice).toLocaleString()}
                                    </span>
                                  </span>
                                  <span className="text-muted-foreground">
                                    Mentor Payout: <span className="font-semibold text-success">
                                      ₹{Number(batch.feeConfig.netPayout || 0).toLocaleString()}
                                    </span>
                                  </span>
                                </div>
                              </div>
                            )}

                            {batch.venue?.city && (
                              <div className="p-3 rounded-xl bg-muted/40 border border-border">
                                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Venue</p>
                                <p className="text-xs text-foreground">
                                  {[batch.venue.street, batch.venue.city, batch.venue.state, batch.venue.pincode].filter(Boolean).join(', ')}
                                </p>
                                {batch.venue.maps_link && (
                                  <a href={batch.venue.maps_link} target="_blank" rel="noreferrer"
                                    className="text-[11px] text-info underline mt-0.5 block">View on Maps</a>
                                )}
                              </div>
                            )}

                            {batch.adminReviewNotes && (
                              <div className="p-3 rounded-xl bg-warning/8 border border-warning/20">
                                <p className="text-[11px] font-semibold text-warning uppercase tracking-wide mb-1">Admin Note</p>
                                <p className="text-xs text-foreground">{batch.adminReviewNotes}</p>
                              </div>
                            )}
                          </div>
                        )}

                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ---- Main Page ----
// Which featureAccess key each Mentors tab needs. Tabs without a granted feature
// are hidden, so a startup with only "Partner-assigned Batches" sees just that tab.
const TAB_FEATURE = {
  ourmento: 'mentors',
  programs: 'mentors',
  batches:  'batches_self',
  ideas:    'mentors',
  explore:  'mentors',
  analytics:'mentors',
};

export default function Mentors() {
  const { startup } = useSTNAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const canTab = (t) => hasFeature(startup, TAB_FEATURE[t]);
  const firstAllowedTab = Object.keys(TAB_FEATURE).find(canTab) || 'ourmento';
  const requestedTab = searchParams.get('tab') || 'ourmento';
  const tabParam    = canTab(requestedTab) ? requestedTab : firstAllowedTab;
  const highlightId = searchParams.get('id') || '';
  const [activeTab, setActiveTab] = useState(tabParam);

  // ── Mentor list state ───────────────────────────────────────────────────────
  const [ourMentors,  setOurMentors]  = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError,   setListError]   = useState(null);

  // ── Dialog/sheet state ──────────────────────────────────────────────────────
  const [onboardOpen,    setOnboardOpen]    = useState(false);
  const [emailPreview,   setEmailPreview]   = useState(null);
  const [analyticsSheet, setAnalyticsSheet] = useState(null);
  const [calendarMentor, setCalendarMentor] = useState(null);
  const [suggestOpen,    setSuggestOpen]    = useState(false);
  const [hostWebinarMentor, setHostWebinarMentor] = useState(null); // active mentor being used to host a webinar
  const [requestCohortOpen, setRequestCohortOpen] = useState(false);
  const [search,         setSearch]         = useState('');
  const [resending,      setResending]      = useState(null);   // profileId being resent
  const [togglingId,     setTogglingId]     = useState(null);   // profileId being toggled

  // ── Fetch mentor list ───────────────────────────────────────────────────────
  const fetchMentors = async () => {
    setListLoading(true);
    setListError(null);
    try {
      const data = await apiListMentors();
      const list = Array.isArray(data) ? data : (data?.mentors || []);
      setOurMentors(list.map(m => ({
        ...m,
        id:       m._id,
        sessions: m.sessions  || 0,
        hours:    m.hours     || 0,
        mentees:  m.mentees   || 0,
        rating:   m.rating    || 0,
        satisfaction:   m.satisfaction   || 0,
        sessionHistory: m.sessionHistory || [],
        monthlyData:    m.monthlyData    || [],
        assignedPrograms: m.assignedPrograms || [],
        joined: m.createdAt
          ? new Date(m.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
          : null,
      })));
    } catch (err) {
      setListError(err.message || 'Failed to load mentors');
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => { fetchMentors(); }, []);

  // ── Sync URL ⇄ tab state ────────────────────────────────────────────────────
  useEffect(() => { setActiveTab(tabParam); }, [tabParam]);
  const handleTabChange = (v) => {
    setActiveTab(v);
    const next = new URLSearchParams(searchParams);
    next.set('tab', v);
    if (!['programs', 'batches', 'ideas'].includes(v)) next.delete('id');
    setSearchParams(next, { replace: true });
  };

  // ── Toggle visibility ───────────────────────────────────────────────────────
  const toggleVisibility = async (mentor) => {
    const next = !mentor.isPublic;
    setTogglingId(mentor._id);
    setOurMentors(prev => prev.map(m => m._id === mentor._id ? { ...m, isPublic: next } : m));
    try {
      await apiPatchVisibility(mentor._id, next);
      toast.success(`${mentor.name} is now ${next ? 'publicly visible' : 'private'}`);
    } catch (err) {
      // revert
      setOurMentors(prev => prev.map(m => m._id === mentor._id ? { ...m, isPublic: !next } : m));
      toast.error(err.message || 'Failed to update visibility');
    } finally {
      setTogglingId(null);
    }
  };

  // ── Resend invite ───────────────────────────────────────────────────────────
  const handleResend = async (mentor) => {
    setResending(mentor._id);
    try {
      const result = await apiResendInvite(mentor._id);
      toast.success(`Invite resent to ${mentor.email}`);
      setEmailPreview({ ...mentor, tempPassword: result.tempPassword });
    } catch (err) {
      toast.error(err.message || 'Failed to resend invite');
    } finally {
      setResending(null);
    }
  };

  // ── New invite success ──────────────────────────────────────────────────────
  const handleOnboardSuccess = (form) => {
    setOnboardOpen(false);
    setEmailPreview({
      _id:          form.mentorId || Date.now(),
      id:           form.mentorId || Date.now(),
      name:         form.name,
      email:        form.email,
      expertise:    form.expertise || [],
      experience:   Number(form.experience) || 0,
      rate:         Number(form.rate) || 0,
      isPublic:     form.isPublic || false,
      nexusNetworkPublic: form.nexusNetworkPublic || false,
      allowExternalMentoring: form.allowExternalMentoring || false,
      tempPassword: form.tempPassword,
      status:       'Pending',
    });
    toast.success(`Mentor invited! Email sent to ${form.email}`);
    fetchMentors(); // refresh list
  };

  const publicFiltered = PUBLIC_MENTORS.filter(m =>
    !search || m.name.toLowerCase().includes(search.toLowerCase()) ||
    m.expertise.some(e => e.toLowerCase().includes(search.toLowerCase()))
  );

  const activeMentors  = ourMentors.filter(m => isActive(m.status));
  const pendingMentors = ourMentors.filter(m => isPending(m.status) || isReview(m.status));
  const totalSessions  = ourMentors.reduce((s, m) => s + (m.sessions || 0), 0);
  const totalHours     = ourMentors.reduce((s, m) => s + (m.hours || 0), 0);
  const ratedMentors   = ourMentors.filter(m => m.rating > 0);
  const avgRating      = ratedMentors.length > 0
    ? (ratedMentors.reduce((s, m) => s + m.rating, 0) / ratedMentors.length).toFixed(1)
    : '—';

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">MentorNexus</h1>
          <p className="text-muted-foreground text-sm mt-1">Onboard mentors, manage visibility, and track mentorship analytics</p>
        </div>
        <div className="flex items-center gap-2">
          {hasFeature(startup, 'webinars') && (
            <Button variant="outline" className="border-border gap-2"
              onClick={() => activeMentors.length > 0 ? setHostWebinarMentor(activeMentors[0]) : setRequestCohortOpen(true)}>
              <Video className="w-4 h-4 text-primary" /> Host Webinar
            </Button>
          )}
          <Button variant="outline" className="border-border gap-2" onClick={() => setSuggestOpen(true)} data-testid="header-suggest-idea-btn">
            <Lightbulb className="w-4 h-4 text-warning" /> Suggest Idea
          </Button>
          <Button className="gradient-primary text-primary-foreground border-0 gap-2" onClick={() => setOnboardOpen(true)}>
            <Plus className="w-4 h-4" /> Onboard Mentor
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Active Mentors', value: activeMentors.length, icon: GraduationCap, color: 'text-primary', bg: 'bg-primary/10' },
          { label: 'Sessions Completed', value: totalSessions, icon: Calendar, color: 'text-chart-2', bg: 'bg-chart-2/10' },
          { label: 'Total Hours', value: `${totalHours}h`, icon: Clock, color: 'text-chart-3', bg: 'bg-chart-3/10' },
          { label: 'Avg. Rating', value: avgRating, icon: Star, color: 'text-chart-4', bg: 'bg-chart-4/10' },
        ].map(s => (
          <Card key={s.label} className="border-border kpi-glow">
            <CardContent className="p-4">
              <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center mb-3', s.bg)}>
                <s.icon className={cn('w-4 h-4', s.color)} />
              </div>
              <div className="text-xl font-bold text-foreground">{s.value}</div>
              <div className="text-xs text-muted-foreground">{s.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="bg-muted flex-wrap h-auto gap-1">
          {canTab('ourmento') && (
            <TabsTrigger value="ourmento" data-testid="tab-ourmento">
              Our Mentors {!listLoading && `(${ourMentors.length})`}
              {pendingMentors.length > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-warning text-[10px] font-bold text-warning-foreground">{pendingMentors.length}</span>
              )}
            </TabsTrigger>
          )}
          {canTab('programs') && <TabsTrigger value="programs" data-testid="tab-programs">Programs</TabsTrigger>}
          {canTab('batches') && <TabsTrigger value="batches" data-testid="tab-batches">Batches</TabsTrigger>}
          {canTab('ideas') && <TabsTrigger value="ideas" data-testid="tab-ideas">Ideas Inbox</TabsTrigger>}
          {canTab('explore') && <TabsTrigger value="explore" data-testid="tab-explore">Explore MentorNexus ({PUBLIC_MENTORS.length})</TabsTrigger>}
          {canTab('analytics') && <TabsTrigger value="analytics" data-testid="tab-analytics">Reports & Analytics</TabsTrigger>}
        </TabsList>

        {/* Our Mentors Tab */}
        <TabsContent value="ourmento" className="mt-6">
          <div className="space-y-3">
            {/* Loading skeleton */}
            {listLoading && [1, 2, 3].map(i => (
              <Card key={i} className="border-border">
                <CardContent className="p-5">
                  <div className="flex items-start gap-4 animate-pulse">
                    <div className="w-12 h-12 rounded-full bg-muted flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-muted rounded w-40" />
                      <div className="h-3 bg-muted rounded w-64" />
                      <div className="h-3 bg-muted rounded w-48" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Error state */}
            {!listLoading && listError && (
              <Card className="border-destructive/30 bg-destructive/5">
                <CardContent className="p-5 flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-destructive">Failed to load mentors</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{listError}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchMentors} className="gap-1">
                    <RefreshCw className="w-3.5 h-3.5" /> Retry
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Empty state */}
            {!listLoading && !listError && ourMentors.length === 0 && (
              <Card className="border-dashed border-border">
                <CardContent className="p-12 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
                    <GraduationCap className="w-7 h-7 text-muted-foreground" />
                  </div>
                  <p className="font-semibold text-foreground mb-1">No mentors yet</p>
                  <p className="text-sm text-muted-foreground mb-4">Invite your first mentor to get started</p>
                  <Button className="gradient-primary text-primary-foreground border-0 gap-2" onClick={() => setOnboardOpen(true)}>
                    <Plus className="w-4 h-4" /> Onboard Mentor
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Pending / Under Review mentors — shown at top with highlight */}
            {!listLoading && pendingMentors.length > 0 && (
              <div className="rounded-xl border border-warning/30 bg-warning/5 divide-y divide-warning/10 overflow-hidden">
                <div className="px-4 py-2.5 flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5 text-warning" />
                  <span className="text-xs font-semibold text-warning">Pending Mentors ({pendingMentors.length}) — awaiting action</span>
                </div>
                {pendingMentors.map(mentor => {
                  const meta = statusMeta(mentor.status);
                  const isResending = resending === mentor._id;
                  return (
                    <div key={mentor._id} className="px-4 py-4 flex items-start gap-3 hover:bg-warning/5 transition-colors">
                      <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center text-xs font-bold text-primary-foreground flex-shrink-0">
                        {mentor.name.split(' ').slice(0, 2).map(n => n[0]).join('')}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <span className="font-semibold text-sm text-foreground">{mentor.name}</span>
                          <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium', meta.cls)}>{meta.label}</span>
                          {isReview(mentor.status) && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <CheckCircle className="w-3 h-3 text-info" /> Onboarding submitted
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">{mentor.email}</p>
                        {mentor.expertise?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {mentor.expertise.slice(0, 3).map(e => <span key={e} className="badge-muted text-[11px]">{e}</span>)}
                            {mentor.expertise.length > 3 && <span className="badge-muted text-[11px]">+{mentor.expertise.length - 3}</span>}
                          </div>
                        )}
                        {mentor.joined && <p className="text-[11px] text-muted-foreground mt-1">Invited {mentor.joined}</p>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {isPending(mentor.status) && (
                          <Button
                            size="sm" variant="outline"
                            className="border-warning/40 text-warning hover:bg-warning/10 gap-1.5 h-8"
                            disabled={isResending}
                            onClick={() => handleResend(mentor)}
                          >
                            {isResending
                              ? <><RefreshCw className="w-3 h-3 animate-spin" /> Sending…</>
                              : <><Send className="w-3 h-3" /> Resend Email</>
                            }
                          </Button>
                        )}
                        {isReview(mentor.status) && (
                          <span className="text-[11px] text-muted-foreground bg-muted/50 px-2.5 py-1 rounded-full border border-border flex items-center gap-1">
                            <Clock className="w-3 h-3" /> Admin review pending
                          </span>
                        )}
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setEmailPreview(mentor)}>
                          <Mail className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Active mentors */}
            {!listLoading && activeMentors.map(mentor => {
              const meta      = statusMeta(mentor.status);
              const toggling  = togglingId === mentor._id;
              return (
                <Card key={mentor._id} className="border-border hover:shadow-card transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full gradient-primary flex items-center justify-center text-sm font-bold text-primary-foreground flex-shrink-0">
                        {mentor.name.split(' ').slice(0, 2).map(n => n[0]).join('')}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-semibold text-foreground">{mentor.name}</span>
                          <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium', meta.cls)}>{meta.label}</span>
                          <span className={cn('text-xs px-2.5 py-0.5 rounded-full font-medium flex items-center gap-1 border',
                            mentor.isPublic ? 'bg-success/10 text-success border-success/25' : 'bg-muted text-muted-foreground border-border'
                          )}>
                            {mentor.isPublic ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                            {mentor.isPublic ? 'Public' : 'Private'}
                          </span>
                          {mentor.allowExternal && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-chart-2/10 text-chart-2 border border-chart-2/20 font-medium">External OK</span>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {(mentor.expertise || []).map(e => <span key={e} className="badge-primary text-xs">{e}</span>)}
                        </div>

                        <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                          {mentor.rating > 0 && <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-warning text-warning" />{mentor.rating}</span>}
                          <span>{mentor.sessions || 0} sessions</span>
                          <span>{mentor.hours || 0}h total</span>
                          <span>{mentor.mentees || 0} mentees</span>
                          {mentor.approvedRateMin != null
                            ? <span className="text-success font-medium">
                                ₹{Number(mentor.approvedRateMin).toLocaleString()}
                                {mentor.approvedRateMax != null && mentor.approvedRateMax !== mentor.approvedRateMin
                                  ? `–₹${Number(mentor.approvedRateMax).toLocaleString()}`
                                  : ''}/hr
                              </span>
                            : mentor.submittedRate > 0
                              ? <span className="text-muted-foreground">₹{Number(mentor.submittedRate).toLocaleString()}/hr <span className="text-[10px] opacity-60">(submitted)</span></span>
                              : null
                          }
                          {mentor.joined && <span>Joined {mentor.joined}</span>}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => !toggling && toggleVisibility(mentor)}
                          title={mentor.isPublic ? 'Make private' : 'Make public'}
                          disabled={toggling}
                          className={cn('p-2 rounded-lg border transition-all', toggling && 'opacity-50 cursor-not-allowed',
                            mentor.isPublic ? 'border-success/30 bg-success/10 text-success hover:bg-success/20' : 'border-border bg-muted text-muted-foreground hover:border-primary/30')}
                        >
                          {toggling ? <RefreshCw className="w-4 h-4 animate-spin" /> : mentor.isPublic ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        <Button variant="outline" size="sm" className="border-border gap-1" onClick={() => setCalendarMentor(mentor)}>
                          <Calendar className="w-3.5 h-3.5" /> Calendar
                        </Button>
                        <Button variant="outline" size="sm" className="border-border gap-1" onClick={() => setAnalyticsSheet(mentor)}>
                          <BarChart3 className="w-3.5 h-3.5" /> Analytics
                        </Button>
                        {hasFeature(startup, 'webinars') && (
                          <Button variant="outline" size="sm" className="border-primary/30 text-primary gap-1" onClick={() => setHostWebinarMentor(mentor)}>
                            <Video className="w-3.5 h-3.5" /> Host Webinar
                          </Button>
                        )}
                        <Button size="sm" className="gradient-primary text-primary-foreground border-0 gap-1" onClick={() => toast.success(`Session request sent to ${mentor.name}`)}>
                          <MessageSquare className="w-3.5 h-3.5" /> Request Session
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* Rejected mentors */}
            {!listLoading && ourMentors.filter(m => m.status === 'Rejected').map(mentor => (
              <Card key={mentor._id} className="border-destructive/20 bg-destructive/5 opacity-75">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground flex-shrink-0">
                      {mentor.name.split(' ').slice(0, 2).map(n => n[0]).join('')}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-foreground">{mentor.name}</span>
                        <span className="badge-destructive text-xs">Rejected</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{mentor.email}</p>
                    </div>
                    <Button size="sm" variant="outline" className="border-border gap-1 h-8" onClick={() => handleResend(mentor)} disabled={resending === mentor._id}>
                      {resending === mentor._id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                      Re-invite
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}

            <button
              onClick={() => setOnboardOpen(true)}
              className="w-full p-5 rounded-xl border-2 border-dashed border-border hover:border-primary/40 hover:bg-muted/40 transition-all flex items-center justify-center gap-3 text-muted-foreground hover:text-foreground"
            >
              <Plus className="w-4 h-4" /> Onboard New Mentor
            </button>
          </div>
        </TabsContent>

        {/* Programs Tab */}
        <TabsContent value="programs" className="mt-6">
          <DynamicProgramsTab mentors={ourMentors} listLoading={listLoading} />
        </TabsContent>

        {/* Batches Tab */}
        <TabsContent value="batches" className="mt-6">
          <DynamicBatchesTab mentors={ourMentors} listLoading={listLoading} />
        </TabsContent>

        {/* Ideas Inbox Tab */}
        <TabsContent value="ideas" className="mt-6">
          <IdeasSection highlightId={activeTab === 'ideas' ? highlightId : ''} onSuggest={() => setSuggestOpen(true)} />
        </TabsContent>

        {/* Explore Tab */}
        <TabsContent value="explore" className="mt-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="relative flex-1 max-w-sm">
              <BookOpen className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search by name, expertise..." className="pl-9" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <span className="badge-info">{publicFiltered.length} mentors available</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {publicFiltered.map(mentor => (
              <Card key={mentor.id} className="border-border hover:shadow-card transition-shadow flex flex-col">
                <CardContent className="p-5 flex flex-col h-full">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-11 h-11 rounded-full gradient-primary flex items-center justify-center text-sm font-bold text-primary-foreground flex-shrink-0">
                      {mentor.name.split(' ').slice(0, 2).map(n => n[0]).join('')}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm text-foreground">{mentor.name}</p>
                        <span className={mentor.available ? 'badge-success' : 'badge-muted'}>{mentor.available ? 'Available' : 'Busy'}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{mentor.exp} yrs exp • {mentor.city}</p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-0.5">
                        <Star className="w-3.5 h-3.5 fill-warning text-warning" />
                        <span className="text-sm font-bold text-foreground">{mentor.rating}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">({mentor.reviews})</p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-3 flex-1">
                    {mentor.expertise.map(e => <span key={e} className="badge-muted">{e}</span>)}
                  </div>

                  <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{mentor.bio}</p>

                  <div className="flex items-center justify-between mt-auto">
                    <div>
                      <span className="font-bold text-foreground">₹{mentor.rate.toLocaleString()}</span>
                      <span className="text-xs text-muted-foreground">/hr</span>
                      <span className="text-xs text-muted-foreground ml-2">{mentor.sessions} sessions done</span>
                    </div>
                    <Button size="sm" className={cn(mentor.available ? 'gradient-primary text-primary-foreground border-0' : 'border-border')} variant={mentor.available ? 'default' : 'outline'} disabled={!mentor.available}
                      onClick={() => toast.success(`Session request sent to ${mentor.name}!`)}
                    >
                      {mentor.available ? 'Request Session' : 'Not Available'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="mt-6">
          <div className="space-y-6">
            {ourMentors.filter(m => m.sessions > 0).map(mentor => (
              <Card key={mentor.id} className="border-border overflow-hidden">
                <CardContent className="p-0">
                  <div className="flex items-start gap-4 p-5 border-b border-border">
                    <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center text-xs font-bold text-primary-foreground flex-shrink-0">
                      {mentor.name.split(' ').slice(0, 2).map(n => n[0]).join('')}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-foreground">{mentor.name}</p>
                      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mt-0.5">
                        <span>{mentor.sessions} sessions</span>
                        <span>{mentor.hours}h</span>
                        <span>{mentor.mentees} mentees</span>
                        <span className="flex items-center gap-0.5"><Star className="w-3 h-3 fill-warning text-warning" />{mentor.rating}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-primary">{mentor.satisfaction}%</div>
                      <div className="text-xs text-muted-foreground">Satisfaction</div>
                    </div>
                    <Button variant="outline" size="sm" className="border-border" onClick={() => setAnalyticsSheet(mentor)}>
                      Full Report <ChevronRight className="w-3.5 h-3.5 ml-1" />
                    </Button>
                  </div>
                  <div className="px-5 py-4">
                    <p className="text-xs text-muted-foreground mb-3 font-medium">Sessions trend</p>
                    <ResponsiveContainer width="100%" height={80}>
                      <AreaChart data={mentor.monthlyData}>
                        <defs>
                          <linearGradient id={`ag${mentor.id}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="hsl(263,70%,62%)" stopOpacity={0.25} />
                            <stop offset="95%" stopColor="hsl(263,70%,62%)" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="m" tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 6, fontSize: 11 }} />
                        <Area type="monotone" dataKey="s" stroke="hsl(263,70%,62%)" strokeWidth={2} fill={`url(#ag${mentor.id})`} name="Sessions" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <OnboardMentorDialog open={onboardOpen} onClose={() => setOnboardOpen(false)} onSuccess={handleOnboardSuccess} />
      <EmailPreviewDialog open={!!emailPreview} onClose={() => setEmailPreview(null)} mentor={emailPreview} />
      <MentorAnalyticsSheet open={!!analyticsSheet} onClose={() => setAnalyticsSheet(null)} mentor={analyticsSheet} onResend={handleResend} />
      <MentorCalendarModal  open={!!calendarMentor} onClose={() => setCalendarMentor(null)} mentor={calendarMentor} />
      <SuggestIdeaDialog open={suggestOpen} onClose={() => setSuggestOpen(false)} mentors={ourMentors} />
      <HostWebinarModal open={!!hostWebinarMentor} onClose={() => setHostWebinarMentor(null)} mentor={hostWebinarMentor} />
      <RequestCohortWebinarModal
        open={requestCohortOpen}
        onClose={() => setRequestCohortOpen(false)}
        hasActiveMentor={activeMentors.length > 0}
        onInviteMentor={() => { setRequestCohortOpen(false); setOnboardOpen(true); }}
      />
    </div>
  );
}
